import React from "react";
import { useTranslation } from "react-i18next";
import { openPaySheet } from "@/components/pay/PaySheet";
import { usePayJob, useResumeJob } from "@/components/pay/useResumeJob";
import { readPendingJob } from "@/lib/pay-sheet";
import type { TFunction } from "i18next";
import { useUserCredits, refreshUserCredits } from "@/hooks/useUserCredits";
import {
  publishStoryboardProject,
  publishStudioAssets,
  publishStudioProgress,
  publishStudioResult,
  publishClipReview,
  useStudioEditEvents,
  useClipReviewEvents,
} from "@/components/landing/director-studio/studioBus";
import type { BuildNumbers, StoryboardBuild, StoryboardClip, StudioAsset } from "@/components/landing/director-studio/types";
import { artistPositions } from "@/components/landing/director-studio/clipKind";
import { routeArtistMoments, type MomentSlot } from "@/components/landing/director-studio/artistMoments";
import BuildProgress, { type BuildUnit } from "@/components/landing/director-studio/BuildProgress";
import ReasonChips from "@/components/landing/director-studio/ReasonChips";
import { cleanReasons } from "@/lib/corrido-reasons";
import WaitReel, { type ReelItem } from "@/components/landing/WaitReel";
import { AuthImg, useAuthMediaUrl } from "@/lib/authMedia";
import { trackEvent } from "@/lib/analytics";
import { corridoMediaUrl } from "@/lib/corrido-media";
import { errorText, stageText } from "@/lib/corrido-status-text";
import { CORRIDO_UPLOAD_ACCEPT, isAcceptedCorridoPhoto, normalizeCorridoPhotoForUpload } from "@/lib/photo-prep";
import FaceLockPicker from "@/components/facelock/FaceLockPicker";
import FaceLockCastStep from "@/components/facelock/FaceLockCastStep";
import { useFaceLockCrew } from "@/components/facelock/useFaceLockCrew";
import {
  clearFaceLockWait, readFaceLockWait, saveFaceLockWait, type FaceLockAvatar,
} from "@/lib/facelock";
// STILLS FIRST: a storyboard also takes an avatar whose video files still train.
import { boardAvatars, isStillsOnly } from "@/lib/facelock-stills";
import { corridoApi } from "@/lib/corrido-api";
import { ShareCard } from "@/features/reel/ShareCard";
import { takeReelHandoff, toFileList } from "@/features/reel/handoff";
import CorridoLookBar, { CorridoLookChoice, useCorridoLook } from "@/components/landing/CorridoLookBar";
import SiteMarkSwitch from "@/components/pay/SiteMarkSwitch";
import { keepSiteMark } from "@/lib/site-mark";

// Corrido Studio — the CapCut-style music-video workspace that lives INSIDE the
// Director chat console (the ONLY place corrido can be used). Three panels:
//   INPUT ASSETS  — song, artist photos, style, mode
//   PREVIEW       — the selected shot enlarged (or the final video)
//   STORYBOARD    — an always-visible, zoomable clip timeline: select, reorder,
//                   regenerate any shot before approving the render.
// Talks to the render engine via /api/web/corrido/*.

type Shot = { idx: number; kind: string; image: string; name: string; theme?: string | null;
              delivery?: "sing" | "scene" | "crew" | null };
/** The artist plan a NEW board gets (GET /api/web/corrido/v2/health artist_plan).
 *  `counts`: a scenes board's artist count per genre (config.artistCounts). */
type ArtistPlan = { plan: string; count: number; crew: boolean; counts?: Record<string, number> };

/** A new scenes board's artist count for the genre picked. */
function artistCountFor(plan: ArtistPlan, style: string): number {
  const n = plan.counts && plan.counts[style];
  return Number.isInteger(n) ? (n as number) : plan.count;
}
/** An ANIMATED clip awaiting Stage-2 review (the stage where real failures live).
 *  The engine sends {idx, url, kind: "artist"|"broll"|"sing", image, qc}; older
 *  rows sent `clip` for the video. Both are read (normalizeClips). */
type Clip = { idx: number; clip: string; url?: string; v?: number; kind: string; image?: string | null; theme?: string | null;
              qc?: { ok: boolean; problems: string[] } | null;
              /** The worker filled this slot itself (a neutral plate or another
               *  stand-in) and why ("face", "render", "replaced"); null for the
               *  clip made from the scene. */
              replaced?: string | null;
              /** Shown as a slow camera move over the approved still. */
              camera_move?: boolean };
/** A delivered clip the customer should look at: a stand-in, or flagged. */
const needsLook = (c: Clip) => Boolean(c.camera_move) || Boolean(c.replaced) || Boolean(c.qc && !c.qc.ok);
// ONE approval (owner rule 2026-10-05): approving the 36 stills is the
// customer's last decision. The video is animated, checked and cut in one go;
// the clip review ("clips" phase) is only reached by a run reopened from the
// gallery or a cut that failed, and the delivered video keeps its free fix pass.
type Phase = "mode" | "inputs" | "working" | "board" | "animating" | "clips" | "assembling" | "done" | "revising";

const MAX_REROLLS = 10;   // per run; a re-roll is ~10-15 min of GPU
// Artist mode needs 4 to 6 close-ups — the same numbers the engine enforces
// (services/corrido-v2/config.js MIN_SELFIES / MAX_SELFIES, E_SELFIE_COUNT).
// The page says exactly that in both languages and never sends 3 or 7.
const MIN_ARTIST_PHOTOS = 4;
const MAX_ARTIST_PHOTOS = 6;
/** A stable empty list, so effects keyed on the build's photos stay put. */
const NO_PHOTOS: File[] = [];
/** What a build waiting for its new FaceLock keeps (lib/facelock saveFaceLockWait):
 *  the song as the URL it was uploaded to (a File cannot outlive the page). */
type CorridoWaitChoices = { style?: string; beats?: string; songUrl?: string; songName?: string };
// The video price when /api/web/credits has not answered yet: the pricing page
// and shared/config/comfy-services.js both carry 1,000. The confirm dialog
// must always show a number, never a blank gate.
const CORRIDO_RENDER_CREDITS_DEFAULT = 1000;

const C = {
  bg: "#05070b",
  surface: "#0a0e15",
  raised: "#0e141d",
  card: "#101722",
  border: "#2a3648",
  borderHi: "#405068",
  cyan: "#38bdf8",
  pink: "#38bdf8",
  purple: "#0ea5e9",
  white: "#ffffff",
  offwhite: "#e2e8f0",
  muted: "#94a3b8",
  green: "#34d399",
  gold: "#e3b341",
  red: "#f87171",
};

type VisionUse = { kind: string; from: string; as: string };
type VisionSkip = { from: string; reason: string };
type VisionRead = { text: string; moments: Array<{ line: string; text: string; used: VisionUse[]; skipped: VisionSkip[] }> };

/** Where a line lands on a scenes board, in the box help's own words. */
function slotLabel(t: TFunction, slot: MomentSlot): string {
  if (slot.to === "sing") {
    if (!slot.shots) return t("corrido.vision.noShotLeft");
    return slot.shots >= slot.of ? t("corrido.vision.onCameraAll") : t("corrido.vision.onCameraSome", { count: slot.shots });
  }
  // A company line on an action shot makes it a shot with company.
  return slot.to === "crew" || slot.kind === "crew" ? t("corrido.vision.companyShot") : t("corrido.vision.actionShot");
}

/** What the "You on camera" box will use, moment by moment, in the customer's
 *  own words, and what it skips and why. Nothing typed reaches the model.
 *  `plan` (a scenes board): each line says which of his shots it goes on, as
 *  the worker routes it (artistMoments.ts); one line is not "all your shots"
 *  there, it is one shot of the kind it describes. */
function VisionPreview({ read, plan }: { read: VisionRead; plan?: ArtistPlan | null }) {
  const { t } = useTranslation();
  const counted = read.moments.filter((m) => m.line);
  const single = counted.filter((m) => m.text).length === 1;
  const skipped = counted.flatMap((m) => m.skipped);
  const scenes = plan?.plan === "scenes";
  const slots = scenes ? routeArtistMoments(counted.filter((m) => m.text).map((m) => m.text), plan!.count, plan!.crew !== false) : [];
  let nth = 0;
  const label = (m: { text: string }, i: number): string => {
    if (!scenes) return single && m.text ? t("corrido.vision.allShots") : t("corrido.vision.moment", { n: i + 1 });
    if (!m.text) return t("corrido.vision.moment", { n: i + 1 });
    const where = slotLabel(t, slots[nth++]);
    return counted.length > 1 ? `${t("corrido.vision.moment", { n: i + 1 })} · ${where}` : where;
  };
  return (
    <div data-testid="vision-preview" style={{ marginTop: 6, borderRadius: 8, border: `1px solid ${C.border}`, background: C.surface, padding: "8px 10px", fontSize: 12, lineHeight: 1.5 }}>
      <div style={{ fontWeight: 700, color: C.white, marginBottom: 2 }}>{t("corrido.vision.previewTitle")}</div>
      {counted.map((m, i) => (
        <div key={i} style={{ color: m.text ? C.offwhite : C.muted }}>
          <b>{label(m, i)}:</b>{" "}
          {m.text ? m.used.map((u) => u.from).join(" · ") : t("corrido.vision.nothingUsable")}
        </div>
      ))}
      {skipped.length > 0 && (
        <div style={{ marginTop: 4, color: C.muted }}>
          <b>{t("corrido.vision.skippedTitle")}:</b>{" "}
          {skipped.map((x, i) => (
            <span key={i}>{i ? "; " : ""}&ldquo;{x.from}&rdquo; ({t(`corrido.vision.reason.${x.reason}`, { defaultValue: t("corrido.vision.reason.unknown") })})</span>
          ))}
        </div>
      )}
    </div>
  );
}

function authHeaders(json = true): Record<string, string> {
  const h: Record<string, string> = {};
  const tok = localStorage.getItem("auth_token");
  if (tok) h["Authorization"] = `Bearer ${tok}`;
  if (json) h["Content-Type"] = "application/json";
  return h;
}

async function uploadFile(t: TFunction, file: File, type: string): Promise<string> {
  const fd = new FormData();
  fd.append("file", file);
  fd.append("type", type);
  const r = await fetch("/api/web/upload", { method: "POST", headers: authHeaders(false), body: fd });
  const j = await r.json();
  if (!r.ok || !j.url) throw new Error(`${file.name}: ${j.message || j.error || t("corrido.uploadFailed")}`);
  return j.url as string;
}

// Photo type checks and HEIC -> JPEG conversion live in lib/photo-prep (shared
// with the FaceLock dialogs); the corrido upload is unchanged: conversion only.

const imgUrl = (p: string) => corridoMediaUrl(p);
// Re-rolled clips are written to the SAME path (shot_XX.mp4 replaced in place),
// so a path-keyed media cache serves the OLD take forever — the "new thumbnail,
// old preview" bug. `v` (the clip file's mtime, from /clips) makes each take a
// distinct cache key.
const clipUrl = (c: { clip: string; v?: number }) => imgUrl(c.clip) + (c.v ? `&v=${c.v}` : "");

/** Clips as /status and /clips send them → the review shape. The engine's
 *  field for the video is `url` (older rows: `clip`), the index is `idx`
 *  (older rows: `index`); `kind` and `image` are read as sent, `qc` stays
 *  null when the engine did not check the clip. */
/** Whether the customer chose to review this run's clips before the cut. */
const REVIEW_KEY = "corrido_review_clips:";
function rememberReview(runId: string, on: boolean) {
  try {
    if (on) localStorage.setItem(REVIEW_KEY + runId, "1");
    else localStorage.removeItem(REVIEW_KEY + runId);
  } catch { /* private window: the choice holds for this page only */ }
}
function reviewWanted(runId?: string | null): boolean {
  if (!runId) return false;
  try { return localStorage.getItem(REVIEW_KEY + runId) === "1"; } catch { return false; }
}

function normalizeClips(raw: unknown): Clip[] {
  if (!Array.isArray(raw)) return [];
  const out: Clip[] = [];
  for (const c of raw as Array<Record<string, unknown>>) {
    if (!c || typeof c !== "object") continue;
    const idx = Number(c.idx ?? c.index);
    const video = typeof c.url === "string" && c.url ? c.url : typeof c.clip === "string" ? c.clip : "";
    if (!Number.isFinite(idx) || !video) continue;
    out.push({
      idx,
      clip: video,
      url: typeof c.url === "string" ? c.url : undefined,
      v: typeof c.v === "number" ? c.v : undefined,
      kind: typeof c.kind === "string" && c.kind ? c.kind : "broll",
      image: typeof c.image === "string" && c.image ? c.image : null,
      theme: typeof c.theme === "string" ? c.theme : null,
      qc: c.qc && typeof c.qc === "object" ? (c.qc as Clip["qc"]) : null,
      replaced: typeof c.replaced === "string" && c.replaced ? c.replaced : (c.spare === true ? "replaced" : null),
      camera_move: c.camera_move === true,
    });
  }
  return out.sort((a, b) => a.idx - b.idx);
}

// The seven corrido styles — canonical user-facing names. Ids travel to
// /api/web/corrido/* (VALID_STYLES in routes/corrido.js must match). Every
// style now has a preview: the same engine still / poster frame the landing
// page uses for that genre, so the picker shows what each look actually is.
export const STYLE_CARDS: Array<{ id: string; emoji: string; previews: string[] }> = [
  { id: "traditional", emoji: "", previews: ["/promo/still_traditional.jpg"] },
  { id: "romantico", emoji: "", previews: ["/promo/still_romantico.jpg"] },
  { id: "sad-sierreno", emoji: "", previews: ["/promo/still_sadsierreno.jpg"] },
  { id: "tumbado", emoji: "", previews: ["/promo/still_tumbado.jpg"] },
  { id: "trap", emoji: "", previews: ["/promo/poster_trap.jpg"] },
  { id: "narco", emoji: "", previews: ["/promo/poster_narco.jpg"] },
  { id: "belico", emoji: "", previews: ["/promo/poster_belico.jpg"] },
];

// Names and blurbs live in locales/{en,es}.json under `corrido.styles.<id>` —
// the ids are the wire format (VALID_STYLES in routes/corrido.js), the labels
// are UI copy and follow the language toggle.
export const styleLabelFor = (t: TFunction, id: string) =>
  STYLE_CARDS.some((c) => c.id === id) ? t(`corrido.styles.${id}`) : id;

// ---- STORYBOARD RECOVERY -----------------------------------------------------
// A started storyboard must NEVER vanish (field report 2026-07-22: a page reset
// wiped a storyboard + its generated stills with no way back). The stills live
// server-side by run_id and are fetched by path, so persisting the shot list +
// run id is enough to rebuild the whole board on reload with zero backend
// round-trip. We only persist the vulnerable stills-approval ("board") phase;
// once a render starts, the server's /runs + ?resume_corrido flow covers it.
// Legacy key — only cleared now (the auto-recover that read it was removed
// 2026-07-22 because it re-opened old boards on every entry). Cleanup calls
// keep clearing any stale value a prior version may have written.
const CORRIDO_BOARD_KEY = "corrido:board-state-v1";

// ---- IN-FLIGHT JOB RECONNECT -------------------------------------------------
// Field incident 2026-07-22: a reload during a render lost ALL trace of it —
// the poll loop lived only in React state. Every corrido job start now writes
// this record; on mount the flow reconnects to the job (still running -> live
// progress; finished while away -> jumps straight to the right review phase).
const CORRIDO_ACTIVE_JOB_KEY = "corrido:active-job-v1";
const CORRIDO_ACTIVE_JOB_TTL_MS = 16 * 60 * 60 * 1000; // outlasts even the longest render
type ActiveJobKind = "storyboard" | "clips" | "assemble" | "revise" | "reroll" | "replace" | "regen";
/** `exclude`: a fix pass's scenes marked remove, kept so a reload can still cut without them. */
type ActiveJobRecord = { jobId: string; kind: ActiveJobKind; runId?: string; idx?: number; exclude?: number[]; savedAt: number };
/** A video that stopped before it finished, with the scenes already made kept:
 *  how many are ready, and whether finishing it is already paid for. */
type VideoPartial = { ready?: number; expected?: number; missing?: number[]; can_continue?: boolean; free?: boolean };
/** What the owner's recover control is showing (services/corrido-v2 recoverRun). */
type RecoverState = { busy: boolean; found?: number; expected?: number; note?: string };
type CorridoRecoverStatus = { status?: string; error?: string; message?: string; report?: { found?: number; expected?: number } | null };
type CorridoStatusResponse = {
  status?: string;
  progress?: number;
  message?: string;
  error?: string;
  /** Engine error CODE (E_SELFIE_COUNT, E_TIMEOUT, …); translated on the page. */
  error_code?: string;
  result_url?: string;
  /** The delivered cut, once one exists — same file as result_url. */
  video?: { url?: string | null } | null;
  run_id?: string;
  storyboard?: Shot[];
  clips?: Clip[];
  /** Clips a running video job has already published (V2), for the wait reel. */
  live_clips?: Clip[] | null;
  // ---- Corrido V2 additions (absent on legacy-engine jobs) ----
  // The engine reports what it is ACTUALLY doing and how many of the 36 scenes
  // exist. That is what lets the bar move for a real reason instead of sitting
  // at one number, and what lets the copy below say something true when the
  // count is still zero. See docs/corrido-v2-contracts.md §10.
  engine?: string;
  state?: string;
  stage?: string;
  expected_output_count?: number;
  completed_output_count?: number;
  elapsed_ms?: number;
  support_code?: string;
  // ---- Smooth-bar contract (absent on an older server; the bar still moves) ----
  /** generate_images | regen_images | animate_clips | reroll_clips | assemble_video */
  op?: string;
  /** The highest percent the page may animate toward before the next real event. */
  progress_ceiling?: number;
  /** Expected wall time of this job, start to done. */
  expected_total_ms?: number;
  /** Estimated ms remaining when the server answered, or null. */
  eta_ms?: number | null;
  /** A video that ended early with scenes kept (error_code E_VIDEO_PARTIAL). */
  partial?: VideoPartial | null;
  // ---- The fix pass (POST /revise): its re-takes, then the cut chained after them ----
  /** This job belongs to a fix pass (the re-takes, or the cut that ends it). */
  revise?: boolean;
  /** On the re-takes: the cut the server started once they landed, or null. */
  next_job_id?: string | null;
  /** On the re-takes: the scenes marked remove, left out of that cut. */
  excluded_idxs?: number[];
};

/** A status frame and the local time it arrived — what every bar draws from. */
type LiveStatus = { s: CorridoStatusResponse; at: number };
/** Before the first frame (files uploading, job just submitted): an honest
 *  zero that may creep to 3 at most, so the bar is alive and never overshoots
 *  what the server will say first. */
const PENDING_NUMBERS: BuildNumbers = { status: "processing", progress: 0, progress_ceiling: 3 };

/** The server's op for a job -> the kind the reconnect path resumes it as. */
function kindFromOp(op: string | null | undefined): ActiveJobKind | null {
  switch (op) {
    case "generate_images": return "storyboard";
    case "animate_clips": return "clips";
    case "reroll_clips": return "reroll";
    case "regen_images": return "regen";
    case "assemble_video": return "assemble";
    default: return null;
  }
}
/** A /runs row type -> the kind (revise and replace keep their own). */
function kindFromRunType(type: string | null | undefined): ActiveJobKind | null {
  switch (type) {
    case "corrido_storyboard": return "storyboard";
    case "corrido_clips": return "clips";
    case "corrido_reroll": return "reroll";
    case "corrido_regen": return "regen";
    case "corrido_assemble": return "assemble";
    case "corrido_revise": return "revise";
    default: return null;
  }
}
/** Last resort when the server names no op: read what the frame carries. */
function kindFromPayload(s: CorridoStatusResponse): ActiveJobKind {
  if (Array.isArray(s.clips) && s.clips.length) return "clips";
  if (s.result_url || (s.video && s.video.url)) return "assemble";
  return "storyboard";
}
const isInFlight = (status: string | null | undefined) => status === "processing" || status === "queued";
/** A fix pass's re-takes or its cut, however it was found (/runs, another
 *  device), is resumed as the fix pass it is: it ends in the re-cut video. */
const asRevise = (kind: ActiveJobKind, s: CorridoStatusResponse): ActiveJobKind =>
  s.revise && (kind === "reroll" || kind === "assemble") ? "revise" : kind;

/** Fired by api.setToken (src/lib/api.ts) whenever the sign-in token changes. */
const AUTH_CHANGED_EVENT = "faceshot:auth-changed";

/** Re-render when the sign-in token appears or goes. The flow is mounted as a
 *  chat widget whose element never changes, so nothing else re-renders it when
 *  auth resolves after mount — and a flow that thinks it is signed out never
 *  reconnects to the build that is running. Events only, no timer: same-tab
 *  sign-in (api.setToken), another tab ('storage'), coming back to the tab. */
function useAuthTokenWatch(): boolean {
  const read = () => { try { return !!localStorage.getItem("auth_token"); } catch { return false; } };
  const [present, setPresent] = React.useState(read);
  React.useEffect(() => {
    const sync = () => setPresent(read());
    window.addEventListener(AUTH_CHANGED_EVENT, sync);
    window.addEventListener("storage", sync);
    window.addEventListener("focus", sync);
    document.addEventListener("visibilitychange", sync);
    return () => {
      window.removeEventListener(AUTH_CHANGED_EVENT, sync);
      window.removeEventListener("storage", sync);
      window.removeEventListener("focus", sync);
      document.removeEventListener("visibilitychange", sync);
    };
  }, []);
  return present;
}

type CorridoValidateResponse = {
  ready?: boolean;
  /** V2 answers {ok:true, skipped:true}: the photo check happens at /storyboard. */
  ok?: boolean;
  skipped?: boolean;
  message?: string;
  error?: string;
  results?: Array<[string, { status?: string; reason?: string }]>;
};
type CorridoMutationResponse = {
  ok?: boolean;
  /** The engine sends both spellings (services/corrido-v2/service.js _created). */
  jobId?: string;
  job_id?: string;
  message?: string;
  error?: string;
  error_code?: string;
  storyboard?: Shot[];
  clips?: Clip[];
  result_url?: string;
  run_id?: string;
  /** A redraw: false when the typed words had nothing usable and it is a plain redraw. */
  steered?: boolean;
};
/** One id for a started job whichever key the server used, "" when refused. */
const jobIdOf = (r: CorridoMutationResponse | null | undefined): string =>
  String((r && (r.job_id || r.jobId)) || "");
type CorridoClipsResponse = { clips?: Clip[]; partial?: VideoPartial | null };
type CorridoStoryboardStateResponse = { storyboard?: Shot[] };
type ErrorWithMessage = { message?: string };

function getErrorMessage(error: unknown, fallback = "Unknown error"): string {
  if (error instanceof Error && error.message) return error.message;
  if (typeof error === "object" && error && "message" in error) {
    const maybe = error as ErrorWithMessage;
    if (typeof maybe.message === "string" && maybe.message) return maybe.message;
  }
  return fallback;
}

function saveActiveJob(kind: ActiveJobKind, jobId: string, runId?: string, idx?: number, exclude?: number[]) {
  try { localStorage.setItem(CORRIDO_ACTIVE_JOB_KEY, JSON.stringify({ jobId, kind, runId, idx, exclude, savedAt: Date.now() } satisfies ActiveJobRecord)); } catch { /* quota */ }
}
function clearActiveJob() {
  try { localStorage.removeItem(CORRIDO_ACTIVE_JOB_KEY); } catch { /* quota */ }
}
function readActiveJob(): ActiveJobRecord | null {
  try {
    const raw = localStorage.getItem(CORRIDO_ACTIVE_JOB_KEY);
    if (!raw) return null;
    const rec = JSON.parse(raw) as ActiveJobRecord;
    if (!rec?.jobId || typeof rec.savedAt !== "number") return null;
    if (Date.now() - rec.savedAt > CORRIDO_ACTIVE_JOB_TTL_MS) { clearActiveJob(); return null; }
    return rec;
  } catch { return null; }
}

// ---- IMMEDIATE STORYBOARD ----------------------------------------------------
// The board fills with placeholder tiles the instant the build starts, instead
// of showing a lone progress bar — the storyboard is "created" immediately and
// its stills stream in as they render. Counts mirror the engine's plan: the
// artist count of the plan new boards get (4 on today's board, the scenes
// plan's own count) and b-roll for the rest; all b-roll when faceless.
const skeletonPlan = (artist: boolean, artistCount = 4) => ({ artist: artist ? artistCount : 0, broll: artist ? 36 - artistCount : 36 });
function buildSkeletonClips(t: TFunction, runId: string, artist: boolean, real: Shot[], artistCount = 4): StoryboardClip[] {
  const byIdx = new Map<number, Shot>(real.map((s) => [s.idx, s] as [number, Shot]));
  const plan = skeletonPlan(artist, artistCount);
  const artistAt = new Set(artistPositions(plan.artist));
  const total = Math.max(plan.artist + plan.broll, real.length);
  const out: StoryboardClip[] = [];
  for (let i = 0; i < total; i++) {
    const r = byIdx.get(i);
    out.push(r
      ? {
          id: `${runId || "building"}:${i}`, idx: i,
          kind: (r.kind === "broll" ? "broll" : r.kind === "anchor" ? "anchor" : "artist"),
          delivery: r.delivery ?? null,
          imageUrl: imgUrl(r.image), theme: r.theme ?? null, durationSec: 3, status: "rendering",
        }
      : {
          id: `building:${i}`, idx: i,
          kind: artistAt.has(i) ? "artist" : "broll",
          imageUrl: "", theme: t("corrido.renderingScene"), durationSec: 3, status: "rendering",
        });
  }
  return out;
}

function PanelLabel({ children }: { children: React.ReactNode }) {
  return (
    <div style={{ fontSize: 11, letterSpacing: 2.5, color: C.cyan, fontWeight: 700, fontFamily: "'Rajdhani', sans-serif", marginBottom: 8 }}>
      {children}
    </div>
  );
}

function CreditsChip({ balance }: { balance: number | null }) {
  const { t } = useTranslation();
  return (
    <div
      data-testid="corrido-credits-chip" style={{
        display: "inline-flex", alignItems: "center", gap: 6,
        border: `1px solid ${C.borderHi}`, borderRadius: 999, padding: "4px 10px",
        background: "rgba(227,179,65,.08)", flexShrink: 0,
      }}
      title={t("corrido.creditsChipTitle")} >
      <span style={{ fontSize: 13 }}></span>
      <span style={{ fontSize: 13, fontWeight: 800, color: C.gold, fontFamily: "'Rajdhani', sans-serif", letterSpacing: 0.5 }}>
        {balance === null ? "—" : balance.toLocaleString()} {t("corrido.creditsUnit")}
      </span>
    </div>
  );
}

// The flow, under the look bar. A look switch (realistic <-> 3D animated,
// lib/corrido-api.ts) re-opens the flow from scratch on the other product:
// its runs, its board and its polls all belong to the product it started on.
export default function CorridoChatFlow() {
  const look = useCorridoLook();
  return (
    <>
      <CorridoLookBar />
      <CorridoChatFlowBody key={look} />
    </>
  );
}

function CorridoChatFlowBody() {
  const { t } = useTranslation();
  const [phase, setPhase] = React.useState<Phase>("mode");
  // Cancel support: the id currently being polled + the interval handle, so the
  // customer can always stop a render (or clear a job that never really began).
  const [activeJobId, setActiveJobId] = React.useState<string>("");
  const [cancelling, setCancelling] = React.useState(false);
  const pollTimer = React.useRef<ReturnType<typeof setInterval> | null>(null);
  // Bumped by every stop: a status answer that was already in flight when its
  // poll was stopped (cancel, or a new poll replacing it) is dropped instead of
  // stopping the NEW poll or painting the old job's numbers on the new bar.
  const pollGen = React.useRef(0);
  const stopPoll = React.useCallback(() => {
    pollGen.current += 1;
    if (pollTimer.current) { clearInterval(pollTimer.current); pollTimer.current = null; }
  }, []);
  React.useEffect(() => stopPoll, [stopPoll]);
  // A build started here keeps running after this widget unmounts (the server
  // finishes it); only the polling stops. Without this, a start() whose POST
  // answered after unmount opened a fresh interval nobody could ever clear.
  const mountedRef = React.useRef(true);
  React.useEffect(() => {
    mountedRef.current = true;
    return () => { mountedRef.current = false; };
  }, []);
  // The few seconds between Build and the server's job id (uploads, photo
  // check, a submit that waits for the studio) are the only stretch where
  // leaving the page can lose the start. Ask before leaving then; once the job
  // exists it finishes on the server and the page reconnects on return.
  React.useEffect(() => {
    if (phase !== "working" || activeJobId) return;
    const warn = (e: BeforeUnloadEvent) => { e.preventDefault(); e.returnValue = ""; };
    window.addEventListener("beforeunload", warn);
    return () => window.removeEventListener("beforeunload", warn);
  }, [phase, activeJobId]);
  const [artistMode, setArtistMode] = React.useState<boolean | null>(null);
  const [song, setSong] = React.useState<File | null>(null);
  const [photos, setPhotos] = React.useState<File[]>([]);
  const [style, setStyle] = React.useState("belico");
  const [beats, setBeats] = React.useState("");
  // The artist plan a new board gets: on the "scenes" plan the genre puts him
  // in the video's scenes, the box help says where each kind of line goes,
  // and the placeholder board has his tiles where the engine will draw them.
  const [artistPlan, setArtistPlan] = React.useState<ArtistPlan | null>(null);
  React.useEffect(() => {
    const ctrl = new AbortController();
    fetch(corridoApi("/v2/health"), { signal: ctrl.signal })
      .then((r) => (r.ok ? r.json() : null))
      .then((j: { artist_plan?: ArtistPlan } | null) => {
        const p = j && j.artist_plan;
        if (p && typeof p.plan === "string" && Number.isInteger(p.count)) {
          setArtistPlan({ plan: p.plan, count: p.count, crew: p.crew !== false,
                          ...(p.counts && typeof p.counts === "object" ? { counts: p.counts } : {}) });
        }
      })
      .catch(() => { /* today's plan and copy stand */ });
    return () => ctrl.abort();
  }, []);
  const scenesPlan = artistPlan?.plan === "scenes";
  // The board this genre gets: its own artist count (21-25 of 36).
  const stylePlan = React.useMemo(
    () => (artistPlan ? { ...artistPlan, count: artistCountFor(artistPlan, style) } : null), [artistPlan, style]);
  // "You on camera" preview: the server reads the box exactly as the build will
  // (services/corrido-v2/vision.js) and says what it uses and what it skips.
  // Read for the genre picked: a grill only in trap and tumbado.
  const [visionRead, setVisionRead] = React.useState<VisionRead | null>(null);
  React.useEffect(() => {
    const text = beats.trim();
    if (!text) { setVisionRead(null); return; }
    const ctrl = new AbortController();
    const timer = window.setTimeout(() => {
      fetch(corridoApi("/v2/vision/preview"), {
        method: "POST", headers: authHeaders(), body: JSON.stringify({ text, style }), signal: ctrl.signal,
      })
        .then((r) => (r.ok ? r.json() : null))
        .then((j: VisionRead | null) => { if (j && Array.isArray(j.moments)) setVisionRead(j); })
        .catch(() => { /* preview is best-effort; the build reads the box itself */ });
    }, 400);
    return () => { window.clearTimeout(timer); ctrl.abort(); };
  }, [beats, style]);
  // There is no render-quality or speed choice: the engine picks the animation
  // steps itself and reads nothing the page could send about it (the old
  // `speed_broll` field went out under a name nothing consumed). No toggle is
  // shown, and the copy says what happens instead of offering a choice.
  const [confirmingRender, setConfirmingRender] = React.useState(false);
  /** The customer asked to watch every clip before the cut (off by default:
   *  one approval). Remembered per run, so a reload mid-render still stops on
   *  the clip review instead of cutting. */
  const [reviewClips, setReviewClips] = React.useState(false);
  const reviewClipsRef = React.useRef(false);
  reviewClipsRef.current = reviewClips;
  // Bumped on EVERY Render click on the board (not only the first), so each
  // click brings the chat's copy of the confirmation back into view.
  const [confirmAsk, setConfirmAsk] = React.useState(0);
  const confirmBoxRef = React.useRef<HTMLDivElement | null>(null);
  // When the price question went up. A Confirm that lands within half a second
  // of it (Enter pressed twice, a held key) was not a read price: it is ignored.
  const confirmAskedAt = React.useRef(0);
  // "Continue my video" when finishing it is NOT already paid for: the first
  // press asks, the second confirms.
  const [continueAsk, setContinueAsk] = React.useState(false);
  // Why the last Render click did not start a video. Printed on the board next
  // to the button: the click is answered where it was made, never with nothing.
  const [renderNotice, setRenderNotice] = React.useState<string | null>(null);
  const [sceneNote, setSceneNote] = React.useState("");
  const [note, setNote] = React.useState("");
  const [progress, setProgress] = React.useState(0);
  // The latest status frame of the job being polled, whole — the shared bar
  // (BuildProgress) draws progress, ceiling, counts, time left and elapsed from
  // it, in the chat and on the board alike. null before the first frame.
  const [liveStatus, setLiveStatus] = React.useState<LiveStatus | null>(null);
  const [supportCode, setSupportCode] = React.useState("");
  const [runId, setRunId] = React.useState("");
  const [shots, setShots] = React.useState<Shot[]>([]);
  const [regenning, setRegenning] = React.useState<number | null>(null);
  const [videoUrl, setVideoUrl] = React.useState("");
  const [verdicts, setVerdicts] = React.useState<Array<{ name: string; ok: boolean; reason: string }>>([]);
  // ---- Stage 2: animated clips ----
  const [clips, setClips] = React.useState<Clip[]>([]);
  const [clipApproved, setClipApproved] = React.useState<Set<number>>(new Set());
  const [rerolling, setRerolling] = React.useState<number | null>(null);
  const [rerollsUsed, setRerollsUsed] = React.useState(0);
  const [selectedClipIdx, setSelectedClipIdx] = React.useState<number | null>(null);
  /** The cut in flight was started from the clip review (not straight after animating). */
  const [cutFromReview, setCutFromReview] = React.useState(false);
  // A video of this run that stopped early with scenes kept: "Continue" renders
  // only what is missing. The ref lets the board's (stable) Render handler read it.
  const [videoPartial, setVideoPartial] = React.useState<VideoPartial | null>(null);
  const videoPartialRef = React.useRef<VideoPartial | null>(null);
  videoPartialRef.current = videoPartial;
  // The owner's recovery of a lost video (admin accounts only).
  const [recover, setRecover] = React.useState<RecoverState | null>(null);
  // ---- delivered-video revision (one free pass) ----
  const [revisionUsed, setRevisionUsed] = React.useState(false);
  const [fixMode, setFixMode] = React.useState(false);
  const [fixSelection, setFixSelection] = React.useState<Record<number, "exclude" | "reroll">>({});
  // "What's wrong?" chips in the fix pass, per marked clip, shown for the clip
  // tapped last. Sent with /revise as reasons {"<idx>": [...]}; they only teach.
  const [fixReasons, setFixReasons] = React.useState<Record<number, string[]>>({});
  const [fixFocus, setFixFocus] = React.useState<number | null>(null);
  // "How did it come out?" on the delivered video, once per delivered file.
  const [videoRating, setVideoRating] = React.useState<"fire" | "good" | "off" | null>(null);

  const [selectedIdx, setSelectedIdx] = React.useState<number | null>(null);
  // Per-clip approval (idx set). Every clip must be approved before render —
  // regenerating a clip clears its approval, a fresh storyboard clears all.
  const [approvedIdxs, setApprovedIdxs] = React.useState<Set<number>>(new Set());
  const [inputsOpen, setInputsOpen] = React.useState(true);
  // No per-clip length: the engine cuts every scene to the song's beat and
  // ignored the `clip_durations` the page used to send, so the timeline no
  // longer offers a trim and the request no longer carries one.
  const SCENE_SEC = 3; // display footprint only
  useAuthTokenWatch();
  const loggedIn = !!localStorage.getItem("auth_token");
  const { balance, serviceCosts } = useUserCredits(loggedIn);
  // FaceLock crew: an artist-mode build can star a trained avatar instead of
  // fresh close-ups. The list is empty whenever the feature or its picker is
  // off (or nothing is castable), and then this flow is exactly what it was.
  //
  // REQUIRED (the server's `required`, FACELOCK_CREW_REQUIRED, owner rule
  // 2026-09-30): an artist-mode video is made with an avatar or not at all.
  // The close-up uploader is then REPLACED by the avatar step (pick one, or
  // create one right here), and a build waiting for a new avatar keeps its
  // song, genre and words (lib/facelock saveFaceLockWait) and starts by
  // itself when the avatar is ready while the page is open.
  const { status: crewStatus, crew, refresh: refreshCrew } = useFaceLockCrew(loggedIn, { poll: "required" });
  const castableFaceLocks = React.useMemo(() => boardAvatars(crew), [crew]);
  const faceLockRequired = crewStatus === "on" && crew?.required === true;
  const [faceLockId, setFaceLockId] = React.useState<string | null>(null);
  const chosenFaceLock = castableFaceLocks.find((a) => a.id === faceLockId) || null;
  const usingFaceLock = artistMode === true && chosenFaceLock !== null;
  /** The close-ups this build uses: none while a FaceLock stands in for them. */
  const buildPhotos = usingFaceLock ? NO_PHOTOS : photos;
  /** The avatar a build is waiting for (created here, still training). */
  const [waitingFaceLockId, setWaitingFaceLockId] = React.useState<string | null>(null);
  /** An awaited avatar that did not finish: the step says so and offers to create another. */
  const [failedFaceLockId, setFailedFaceLockId] = React.useState<string | null>(null);
  /** Carry on by itself once ready: the avatar was created here, or seen training while the page was open. */
  const waitArmed = React.useRef(false);
  /** A song kept by a waiting build: the URL it was uploaded to (a File cannot outlive the page). */
  const [savedSong, setSavedSong] = React.useState<{ url: string; name: string } | null>(null);
  const songUpload = React.useRef<{ file: File | null; url: string; promise: Promise<string> | null }>({ file: null, url: "", promise: null });
  const hasSong = !!song || !!savedSong;
  const songLabel: string | null = song?.name ?? savedSong?.name ?? null;
  // The video price. /credits is the source of truth; until it answers the
  // documented default keeps the gate honest instead of silent.
  const renderCost = serviceCosts?.corrido ?? CORRIDO_RENDER_CREDITS_DEFAULT;
  // The delivered video, playable and downloadable through the auth-gated proxy
  // (a bare <a href> to /api/... would 401; the blob works for both tags).
  const playableVideo = useAuthMediaUrl(videoUrl ? imgUrl(videoUrl) : "");
  // SHARE: the video FILE through the phone's own share sheet (WhatsApp,
  // TikTok, Instagram...). There is no public link to share (the video is
  // served behind the customer's sign-in), so only a browser that can share
  // files gets the button. The file is made ready beforehand: a share sheet
  // opened after an await can lose the tap that allowed it.
  const canShareFiles = React.useMemo(() => {
    try {
      return typeof navigator !== "undefined" && typeof navigator.canShare === "function"
        && navigator.canShare({ files: [new File([""], "corrido.mp4", { type: "video/mp4" })] });
    } catch { return false; }
  }, []);
  const [shareFile, setShareFile] = React.useState<File | null>(null);
  const [shareNote, setShareNote] = React.useState("");
  React.useEffect(() => {
    setShareFile(null);
    setShareNote("");
    if (!canShareFiles || !playableVideo) return;
    let live = true;
    fetch(playableVideo).then((r) => r.blob()).then((blob) => {
      if (live) setShareFile(new File([blob], `corrido-${String(runId || "video").slice(0, 8)}.mp4`, { type: "video/mp4" }));
    }).catch(() => { /* Download still works */ });
    return () => { live = false; };
  }, [canShareFiles, playableVideo, runId]);
  // What the customer did with the delivered video (rated it, downloaded it,
  // shared it) teaches the tool which of its pictures and clips people keep.
  // Fire and forget: never awaited, never in the way of the download or the
  // share, and a failure says nothing.
  const sendFeedback = (event: "video_rated" | "video_downloaded" | "video_shared", extra: Record<string, unknown> = {}) => {
    const rid = runIdRef.current || runId;
    if (!rid) return;
    try {
      void fetch(corridoApi("/v2/feedback"), {
        method: "POST", headers: authHeaders(), keepalive: true,
        body: JSON.stringify({ run_id: rid, event, ...extra }),
      }).catch(() => { /* a lost beacon only loses a lesson */ });
    } catch { /* never in the customer's way */ }
  };
  const rateVideo = (rating: "fire" | "good" | "off") => {
    setVideoRating(rating);
    sendFeedback("video_rated", { rating });
  };
  React.useEffect(() => { setVideoRating(null); }, [videoUrl]);
  const shareVideo = () => {
    if (!shareFile) return;
    setShareNote("");
    navigator.share({ files: [shareFile], title: songLabel || t("corrido.yourSong") }).then(() => sendFeedback("video_shared"), (e: unknown) => {
      // Closing the share sheet is not a failure.
      if (e instanceof DOMException && e.name === "AbortError") return;
      setShareNote(t("corrido.shareFailed"));
    });
  };

  // object URLs for photo thumbnails
  const photoPreviews = React.useMemo(() => photos.map((p) => URL.createObjectURL(p)), [photos]);
  React.useEffect(() => () => { photoPreviews.forEach((u) => URL.revokeObjectURL(u)); }, [photoPreviews]);

  const selectedShot = shots.find((s) => s.idx === selectedIdx) || shots[0] || null;

  // Live refs so the board's (stable) edit handlers reach current state/functions
  // without re-subscribing to the studio bus on every render.
  const regenRef = React.useRef<(idx: number, scene?: string, reasons?: string[]) => void>(() => {});
  const startClipsRef = React.useRef<() => void>(() => {});
  const confirmRunRef = React.useRef<() => void>(() => {});
  const dismissRenderRef = React.useRef<() => void>(() => {});
  // The build start and the mode, for the avatar wait (it continues from an effect).
  const startRef = React.useRef<(castId?: string) => Promise<void>>(async () => {});
  const artistModeRef = React.useRef<boolean | null>(null);
  const regenningRef = React.useRef<number | null>(null);
  const allApprovedRef = React.useRef(false);
  // Current board, readable from async completions (a redraw lands minutes
  // after the click, when the closure's `shots` is long stale).
  const shotsRef = React.useRef<Shot[]>([]);
  /** The run id, readable from async completions (a cut started on a finish). */
  const runIdRef = React.useRef("");
  const phaseRef = React.useRef<Phase>("mode");
  /** The board on the canvas is the building placeholder (publishBuildingBoard),
   *  not a real storyboard — so a build that ends without one can take it down. */
  const buildingBoardUp = React.useRef(false);

  // ---- THE LIVE BUILD, as every bar draws it ----
  // One key per job (its id; "pending:<phase>" while the files still upload),
  // so the chat bar and the board's bars of the same job show the same percent.
  const buildKey = activeJobId || `pending:${phase}`;
  const buildNumbers: BuildNumbers = liveStatus?.s ?? PENDING_NUMBERS;
  const buildReceivedAt = liveStatus?.at ?? 0;
  const buildUnit: BuildUnit | null = phase === "working" || regenning !== null ? "pictures"
    : phase === "animating" || rerolling !== null ? "clips" : null;
  const liveBuild: StoryboardBuild = React.useMemo(() => ({
    jobId: activeJobId, key: buildKey, receivedAt: buildReceivedAt, numbers: buildNumbers, label: note || null, unit: buildUnit,
  }), [activeJobId, buildKey, buildReceivedAt, buildNumbers, note, buildUnit]);
  /** The build as published with the storyboard being drawn (a tick's frame). */
  const buildOf = (jobId: string, s: CorridoStatusResponse | null): StoryboardBuild => ({
    jobId, key: jobId || "pending:working", receivedAt: s ? Date.now() : 0, numbers: s ?? PENDING_NUMBERS, label: null, unit: "pictures",
  });

  // ---- WHILE YOU WAIT: the reel under the progress bar ----
  // Their own creation first, live: the clips this video has already made,
  // else the stills of the board being drawn (or the board being animated),
  // mixed with clips from the studio's showcase and their own earlier videos
  // (GET /showcase) so it moves. Showcase only, until anything of theirs exists.
  const rendering = phase === "working" || phase === "animating" || phase === "assembling" || phase === "revising";
  const [showcase, setShowcase] = React.useState<Array<{ clip: string; image?: string | null; source?: string }>>([]);
  const showcaseAsked = React.useRef(false);
  React.useEffect(() => {
    if (!rendering || showcaseAsked.current || !loggedIn) return;
    showcaseAsked.current = true;
    const q = runId ? `?exclude_run=${encodeURIComponent(runId)}` : "";
    fetch(`${corridoApi("/showcase")}${q}`, { headers: authHeaders(false) })
      .then((r) => (r.ok ? r.json() : null))
      .then((j) => { if (j && Array.isArray(j.clips) && mountedRef.current) setShowcase(j.clips.filter((c: { clip?: unknown }) => typeof c?.clip === "string" && c.clip)); })
      .catch(() => { showcaseAsked.current = false; /* the reel just waits for their own scenes */ });
  }, [rendering, runId, loggedIn]);
  const reelItems: ReelItem[] = React.useMemo(() => {
    if (!rendering) return [];
    const live = normalizeClips(liveStatus?.s.live_clips);
    const ownClips = live.length ? live : (phase === "assembling" || phase === "revising" ? clips : []);
    if (ownClips.length) {
      return ownClips.map((c) => ({ key: `clip:${c.idx}:${c.clip}`, kind: "video" as const, src: clipUrl(c), caption: t("corrido.waitReel.ownClips") }));
    }
    const stillsSrc: Shot[] = phase === "working" ? (Array.isArray(liveStatus?.s.storyboard) ? liveStatus!.s.storyboard! : []) : shots;
    const stills: ReelItem[] = stillsSrc.filter((sh) => sh && sh.image)
      .map((sh) => ({ key: `still:${sh.idx}:${sh.image}`, kind: "image" as const, src: imgUrl(sh.image), caption: t("corrido.waitReel.ownStills") }));
    const reel: ReelItem[] = showcase.map((c, i) => ({
      key: `show:${i}:${c.clip}`, kind: "video" as const, src: imgUrl(c.clip),
      caption: t(c.source === "own" ? "corrido.waitReel.past" : "corrido.waitReel.showcase"),
    }));
    // Alternate one of theirs, one from the reel, so neither runs dry first.
    const out: ReelItem[] = [];
    for (let i = 0; i < Math.max(stills.length, reel.length); i += 1) {
      if (stills[i]) out.push(stills[i]);
      if (reel[i]) out.push(reel[i]);
    }
    return out;
  }, [rendering, phase, liveStatus, clips, shots, showcase, t]);

  /** The delivered video's scenes worth a look, by their position in the cut
   *  (the numbers the fix pass shows): stood in for, and flagged. */
  const lookAt = React.useMemo(() => {
    const moved: number[] = [];
    const replaced: number[] = [];
    const flagged: number[] = [];
    clips.forEach((c, pos) => {
      if (c.camera_move) moved.push(pos + 1);
      else if (c.replaced) replaced.push(pos + 1);
      else if (c.qc && !c.qc.ok) flagged.push(pos + 1);
    });
    return { moved, replaced, flagged, length: moved.length + replaced.length + flagged.length };
  }, [clips]);

  // ---- Studio bus: mirror this flow's state into the always-visible studio
  // chrome (StoryboardTimeline / AssetLibrary) and accept edit intents back.
  React.useEffect(() => {
    // While a storyboard is being drawn the board is publishBuildingBoard's (the
    // placeholder tiles + the live bar). A previous board still in `shots` (a
    // rebuild) used to be republished over it — without the build — whenever
    // the run id or the balance changed, so the board's bar fell back to a
    // pending 0 % until the next poll put it back.
    if (!shots.length || phase === "working") return;
    const clips: StoryboardClip[] = shots.map((sh) => ({
      id: `${runId || "pending"}:${sh.idx}`,
      idx: sh.idx,
      kind: (sh.kind === "broll" ? "broll" : sh.kind === "anchor" ? "anchor" : "artist"),
      delivery: sh.delivery ?? null,
      imageUrl: imgUrl(sh.image),
      theme: sh.theme ?? null,
      durationSec: SCENE_SEC,
      status: regenning === sh.idx ? "regenerating" : (phase === "animating" || phase === "assembling" || phase === "revising") ? "rendering" : phase === "done" ? "rendered" : approvedIdxs.has(sh.idx) ? "approved" : "draft",
    }));
    // Publish the FULL render contract, not just clips — the studio board
    // shows song / photos / style / beats / cost so nothing renders unseen.
    publishStoryboardProject({
      runId,
      service: "corrido",
      clips,
      totalDurationSec: clips.reduce((sum, clip) => sum + clip.durationSec, 0),
      selectedIdx: selectedShot?.idx ?? null,
      songName: songLabel,
      styleId: style,
      styleLabel: styleLabelFor(t, style),
      beats: beats.trim() || null,
      renderCostCredits: renderCost,
      creditBalance: balance,
      // The board only knows the phases of a run; "mode"/"inputs" never reach
      // here in practice (no shots yet) and read as "no run" if they did.
      phase: phase === "mode" || phase === "inputs" ? null : phase,
      // The render gate, drawn on the board under its Render button.
      renderAsk: phase === "board" && confirmingRender,
      renderFree: !!videoPartial?.free,
      renderNotice: phase === "board" ? renderNotice : null,
      photos: buildPhotos.map((p, i) => {
        const verdict = verdicts.find((v) => v.name === p.name);
        return { name: p.name, url: photoPreviews[i] ?? "", ok: verdict ? verdict.ok : null, reason: verdict?.reason ?? null };
      }),
    });
    buildingBoardUp.current = false;
  }, [shots, runId, regenning, phase, selectedShot?.idx, approvedIdxs, songLabel, style, beats, renderCost, balance, buildPhotos, photoPreviews, verdicts, confirmingRender, videoPartial, renderNotice]);

  // A storyboard build that ended with no board to show (cancelled, failed,
  // refused, upload error) takes its building board down with it: left up, it
  // kept a live bar creeping on the canvas for a build that no longer exists.
  // (A real board, when there is one, was just published by the effect above.)
  React.useEffect(() => {
    if (phase === "working" || !buildingBoardUp.current) return;
    buildingBoardUp.current = false;
    publishStoryboardProject(null);
  }, [phase]);

  // Clear the studio mirror when this flow unmounts (e.g. console restart).
  React.useEffect(() => () => {
    publishStoryboardProject(null);
    publishStudioAssets([]);
    publishStudioProgress("corrido", null);
    publishStudioResult("corrido", null);
    publishClipReview(null);
  }, []);

  // Work in flight lives ON the canvas as a CapCut-style loading card.
  React.useEffect(() => {
    const titles: Partial<Record<Phase, string>> = {
      working: t("corrido.progressTitles.working"),
      animating: t("corrido.progressTitles.animating"),
      assembling: t("corrido.progressTitles.assembling"),
      revising: t("corrido.progressTitles.revising"),
    };
    const title = titles[phase];
    if (title) {
      // The card draws the same smooth bar as the chat from the same frame.
      publishStudioProgress("corrido", { title, message: note || t("corrido.working"), progress: progress > 0 ? progress : null, build: liveBuild });
    } else {
      publishStudioProgress("corrido", null);
    }
  }, [phase, note, progress, liveBuild]);

  React.useEffect(() => {
    const assets: StudioAsset[] = [
      ...(songLabel ? [{ id: `song_${songLabel}`, kind: "audio" as const, url: "", name: songLabel, source: "upload" as const }] : []),
      ...buildPhotos.map((p, i) => ({
        id: `photo_${p.name}_${p.size}`,
        kind: "image" as const,
        url: photoPreviews[i],
        name: p.name,
        source: "upload" as const,
      })),
    ];
    publishStudioAssets(assets);
  }, [songLabel, buildPhotos, photoPreviews]);

  useStudioEditEvents({
    onReorder: React.useCallback((order: number[]) => {
      setShots((prev) => {
        const byIdx = new Map(prev.map((s) => [s.idx, s]));
        const next = order.map((idx) => byIdx.get(idx)).filter(Boolean) as Shot[];
        return next.length === prev.length ? next : prev;
      });
    }, []),
    onSelect: React.useCallback((idx: number) => setSelectedIdx(idx), []),
    onApprove: React.useCallback((idx: number) => {
      setRenderNotice(null);
      setApprovedIdxs((prev) => {
        const next = new Set(prev);
        if (next.has(idx)) next.delete(idx); else next.add(idx);
        return next;
      });
    }, []),
    // Board -> flow: regenerate a clip's still. Same code path as the (now
    // removed) in-chat button, so the board is the single place edits happen.
    // Refs keep this handler stable while reading live state.
    onRegen: React.useCallback((idx: number, scene?: string, reasons?: string[]) => {
      if (regenningRef.current !== null) return; // one regen at a time
      void regenRef.current(idx, scene, reasons);
    }, []),
    // Board -> flow: the "Approve & Render" button on the board. ALWAYS through
    // the credit confirmation: animating is the step that charges, and the
    // customer sees the price before it does — never a silent start. And never
    // a silent nothing: the board shows the question (or the reason) itself.
    onRender: React.useCallback(() => {
      if (!allApprovedRef.current) { setRenderNotice(t("studio.timeline.renderNeedsApproval")); return; }
      // Finishing a video whose charge was kept costs nothing: no price to confirm.
      if (videoPartialRef.current?.free) { startClipsRef.current(); return; }
      setRenderNotice(null);
      trackEvent("corrido_board_render_clicked");
      // Only a question that just went up restarts the half second: pressing
      // Render again on an open question must not make Confirm dead again.
      setConfirmingRender((was) => { if (!was) confirmAskedAt.current = Date.now(); return true; });
      setConfirmAsk((n) => n + 1);
    }, [t]),
    // Board -> flow: the board's own "Confirm & run" / "Keep editing".
    onRenderConfirm: React.useCallback(() => confirmRunRef.current(), []),
    onRenderCancel: React.useCallback(() => dismissRenderRef.current(), []),
  });

  // The price was asked for a board with every scene approved and nothing being
  // redrawn. The moment that stops being true (a scene taken back, a redraw
  // started, the board left) the question comes down: Confirm must never start
  // a video the board itself says is not ready.
  React.useEffect(() => {
    if (!confirmingRender) return;
    const ready = phase === "board" && regenning === null && shots.length > 0 && shots.every((s) => approvedIdxs.has(s.idx));
    if (!ready) setConfirmingRender(false);
  }, [confirmingRender, phase, regenning, shots, approvedIdxs]);

  // The chat keeps a copy of the confirmation, inside the chat's own scroll
  // box. Nothing used to scroll to it: on a reopened board it landed below the
  // fold. Move that box only (never the page) so the copy is in view too.
  React.useEffect(() => {
    const el = confirmBoxRef.current;
    const log = el?.closest<HTMLElement>('[data-testid="landing-director-chat-log"]');
    if (!el || !log) return;
    const box = el.getBoundingClientRect();
    const view = log.getBoundingClientRect();
    if (box.bottom > view.bottom - 12) log.scrollTop += box.bottom - view.bottom + 12;
    else if (box.top < view.top + 12) log.scrollTop -= view.top + 12 - box.top;
  }, [confirmAsk]);

  // ---- STAGE-2 CLIP REVIEW mirrored onto the board ----
  // Publish the animated-clip review so watch/approve/re-roll/replace/assemble
  // all happen on the board (a resumed run is edited there, not in the chat).
  const rerollClipRef = React.useRef<(idx: number, reasons?: string[]) => void>(() => {});
  const replaceClipRef = React.useRef<(idx: number, scene?: string, reasons?: string[]) => void>(() => {});
  const assembleRef = React.useRef<() => void>(() => {});
  const rerollingRef = React.useRef<number | null>(null);
  React.useEffect(() => {
    // A cut started by the page itself (no review) shows no review card.
    if (phase !== "clips" && !(phase === "assembling" && cutFromReview)) { publishClipReview(null); return; }
    publishClipReview({
      runId,
      clips: clips.map((c) => ({
        idx: c.idx,
        videoUrl: clipUrl(c),
        thumbUrl: c.image ? imgUrl(c.image) + (c.v ? `&v=${c.v}` : "") : null,
        kind: c.kind,
        theme: c.theme ?? null,
        qc: c.qc ?? null,
      })),
      approved: [...clipApproved],
      rerolling,
      rerollsLeft: MAX_REROLLS - rerollsUsed,
      selectedIdx: selectedClipIdx,
      busy: phase === "assembling",
      // A new take in flight carries its live numbers so the review card can
      // show the same moving bar (a take is 10-15 minutes).
      build: rerolling !== null && activeJobId ? liveBuild : null,
    });
  }, [phase, cutFromReview, clips, clipApproved, rerolling, rerollsUsed, selectedClipIdx, runId, activeJobId, liveBuild]);

  useClipReviewEvents({
    onSelect: React.useCallback((idx: number) => setSelectedClipIdx(idx), []),
    onApprove: React.useCallback((idx: number) => {
      setClipApproved((prev) => { const n = new Set(prev); if (n.has(idx)) n.delete(idx); else n.add(idx); return n; });
    }, []),
    onReroll: React.useCallback((idx: number, reasons?: string[]) => { if (rerollingRef.current === null) void rerollClipRef.current(idx, reasons); }, []),
    onReplace: React.useCallback((idx: number, scene?: string, reasons?: string[]) => { if (rerollingRef.current === null) void replaceClipRef.current(idx, scene, reasons); }, []),
    onUseAllClean: React.useCallback(() => {
      setClips((cur) => { setClipApproved(new Set(cur.filter((c) => !(c.qc && !c.qc.ok)).map((c) => c.idx))); return cur; });
    }, []),
    onAssemble: React.useCallback(() => { void assembleRef.current(); }, []),
  });

  const allApproved = shots.length > 0 && shots.every((s) => approvedIdxs.has(s.idx));

  // Deliberate resume via /?resume_corrido=<run_id> (from the ChopShop
  // gallery). Runs once on mount, BEFORE any new build exists, so it can never
  // eat work in progress — the accident the old in-flow banner caused.
  // Waits for sign-in: the ref is only spent once the token is known, so a
  // session that resolves after mount still resumes.
  const resumeParamRef = React.useRef(false);
  React.useEffect(() => {
    if (resumeParamRef.current || !loggedIn) return;
    resumeParamRef.current = true;
    const rid = new URLSearchParams(window.location.search).get("resume_corrido");
    if (!rid) return;
    (async () => {
      try {
        setNote(t("corrido.restoring"));
        const r = await fetch(`${corridoApi("/clips")}?run_id=${encodeURIComponent(rid)}`, { headers: authHeaders(false) });
        const j: CorridoClipsResponse = await r.json();
        const list: Clip[] = normalizeClips(j.clips);
        if (list.length) {
          setRunId(rid);
          setClips(list);
          setClipApproved(new Set());
          setSelectedClipIdx(list[0]?.idx ?? null);
          setInputsOpen(false);
          setPhase("clips");
          setVideoPartial(j.partial && j.partial.can_continue ? j.partial : null);
          setNote(j.partial && j.partial.can_continue ? partialText(j.partial) : t("corrido.renderRestored"));
        } else {
          // No animated clips yet — this run stopped at (or never left) the
          // STILLS stage. Reopen the storyboard board from the disk manifest
          // instead of dead-ending (recovery for interrupted/failed runs).
          const sr = await fetch(`${corridoApi("/storyboard_state")}?run_id=${encodeURIComponent(rid)}`, { headers: authHeaders(false) });
          const sj: CorridoStoryboardStateResponse = await sr.json();
          const board: Shot[] = sj.storyboard || [];
          if (!board.length) {
            setNote(t("corrido.runNotFound"));
            // Drop the param here too: left in the URL, every later reload
            // skipped the in-flight reconnect (which yields to resume_corrido).
            window.history.replaceState(window.history.state, "", window.location.pathname + "#director-console");
            return;
          }
          setRunId(rid);
          setShots(board);
          setApprovedIdxs(new Set());
          setSelectedIdx(board[0]?.idx ?? null);
          setInputsOpen(false);
          setPhase("board");
          setNote(t("corrido.storyboardRestored"));
        }
        // drop the param so a later refresh of the page doesn't re-trigger.
        // The entry's state stays: its key is where ScrollManager keeps the scroll.
        window.history.replaceState(window.history.state, "", window.location.pathname + "#director-console");
      } catch (e: unknown) {
        setNote(t("corrido.restoreFailed", { reason: getErrorMessage(e) }));
        window.history.replaceState(window.history.state, "", window.location.pathname + "#director-console");
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loggedIn]);

  // NOTE (2026-07-22): the old localStorage board AUTO-RECOVER was removed — it
  // re-opened the previous storyboard every time the user entered the corrido
  // tool, blocking a fresh start. Recovery now lives on the BOARD as selectable
  // cards (the /runs endpoint + ?resume_corrido open them on demand) and via the
  // in-flight active-job reconnect below. Entering the tool is always a clean
  // slate; past storyboards are there to click or delete, never auto-loaded.

  /** The line for a video that stopped early with scenes kept. */
  const partialText = (p: VideoPartial): string => {
    const ready = Number(p.ready || 0);
    const expected = Number(p.expected || 0);
    const lead = Array.isArray(p.missing) && p.missing.length === 0
      ? t("corrido.partialAllReady", { expected })
      : t("corrido.partialReady", { ready, expected });
    return `${lead} ${p.free ? t("corrido.partialFree") : t("corrido.partialPaid", { count: renderCost.toLocaleString() })}`;
  };
  /** A video job that failed: keep what it says about scenes already made.
   *  Returns the line to show, or "" when nothing was kept. */
  const notePartial = (s: CorridoStatusResponse): string => {
    const p = s.error_code === "E_VIDEO_PARTIAL" && s.partial && s.partial.can_continue ? s.partial : null;
    if (!p) return "";
    setVideoPartial(p);
    return partialText(p);
  };

  /** A video job ended without finishing. Scenes it had already made are kept:
   *  show them with "Continue"; with nothing kept, go back to `back`. */
  const videoStopped = (s: CorridoStatusResponse, reason: string, back: Phase) => {
    const line = notePartial(s);
    const kept: Clip[] = line ? normalizeClips(s.clips) : [];
    if (s.run_id) setRunId((prev) => prev || s.run_id);
    if (kept.length && (back !== "board" || !shotsRef.current.length)) {
      setClips(kept); setClipApproved(new Set()); setSelectedClipIdx(kept[0]?.idx ?? null);
      setInputsOpen(false); setPhase("clips");
    } else setPhase(back);
    setNote(line || reason);
    // Under the board's Render button only when the board is where this lands;
    // a later storyboard must not open with an old failure under its button.
    setRenderNotice(back === "board" && shotsRef.current.length ? (line || reason) : null);
  };

  /** Poll one job to its end. `onFail` (optional) replaces the default failure
   *  handling — the default returns the customer to the inputs, which is right
   *  for a storyboard build and wrong for a redraw, a re-take or an assembly,
   *  where the board or the clips must stay exactly where they were. */
  const poll = (
    jobId: string,
    onDone: (s: CorridoStatusResponse) => void,
    onTick?: (s: CorridoStatusResponse) => void,
    onFail?: (s: CorridoStatusResponse, reason: string) => void,
    first?: CorridoStatusResponse,
  ) => {
    // One poll at a time: a second start (a reconnect racing a click) must
    // never leave an orphaned interval polling the old job.
    stopPoll();
    if (!mountedRef.current) return;
    const gen = pollGen.current;
    setActiveJobId(jobId);
    // A frame already in hand (the reconnect read it) shows at once; otherwise
    // the bar starts from the honest pending zero until the first poll.
    setLiveStatus(first ? { s: first, at: Date.now() } : null);
    // A silent catch used to hide EVERY status error, so when the poll started
    // failing the bar just froze at its last number (famously 8%) forever — no
    // error, no finish, no way out. Count consecutive failures and say so.
    let consecutiveErrors = 0;
    let lastError = "";
    const jid = encodeURIComponent(jobId);
    // The run belongs to the product it started on (realism or 3D): its
    // progress is read there for the whole run, even if the look switch moves
    // meanwhile (owner, 2026-10-11: a 3D board "lost connection with the
    // engine" at 2% while the server went on and finished it).
    // Both spellings of the id: routes/corrido.js reads `id`, the V2 router
    // reads `id`/`jobId`, and the contract names `job_id`.
    const statusUrl = `${corridoApi("/status")}?id=${jid}&job_id=${jid}`;
    pollTimer.current = setInterval(async () => {
      if (gen !== pollGen.current) return;
      try {
        const r = await fetch(statusUrl, { headers: authHeaders() });
        if (gen !== pollGen.current) return; // stopped or replaced while waiting
        if (!r.ok) throw new Error(`status ${r.status}`);
        const s: CorridoStatusResponse = await r.json();
        if (gen !== pollGen.current) return;
        consecutiveErrors = 0;
        setProgress(s.progress || 0);
        // Stage CODES translate here (corrido.stages.*); the server's English
        // sentence is only the fallback for a code the page does not know.
        setNote(stageText(t, s));
        // The whole frame: the shared bar reads progress, ceiling, counts, time
        // left and elapsed from it. Fields an older server omits just hide.
        setLiveStatus({ s, at: Date.now() });
        if (onTick) { try { onTick(s); } catch { /* a tick error must never kill the poll */ } }
        if (s.status === "completed") { stopPoll(); setActiveJobId(""); setLiveStatus(null); setSupportCode(""); onDone(s); }
        if (s.status === "failed" || s.status === "cancelled") {
          stopPoll(); clearActiveJob(); setActiveJobId(""); setLiveStatus(null);
          // A terminal failure carries a quotable code. It contains no personal
          // data — just the correlation id and the error class — so support can
          // find the exact job without the customer describing symptoms.
          setSupportCode(s.status === "failed" ? (s.support_code || "") : "");
          // Error CODES translate too (corrido.errors.*) — a Spanish customer
          // never reads an English engine sentence at the worst moment.
          const reason = s.status === "cancelled" ? t("corrido.cancelled") : errorText(t, s);
          if (onFail) onFail(s, reason);
          else { setNote(reason); setPhase("inputs"); }
          refreshUserCredits();
        }
      } catch (err) {
        if (gen !== pollGen.current) return;
        consecutiveErrors += 1;
        lastError = err instanceof Error ? err.message : "network";
        // ~2min of failures: the render is not reporting back. Say so and let
        // them Cancel, instead of a bar that sits at one number indefinitely.
        // The last answer rides along so the next report names its cause.
        if (consecutiveErrors === 30) {
          setNote(`${t("corrido.lostContact")} (${lastError})`);
        }
      }
    }, 4000);
  };

  /** Stop a render the customer no longer wants — including one whose submit
   *  never reached the engine. Always clears the screen locally even if the
   *  server call fails, so a job can never trap the UI again. */
  const cancelRender = async () => {
    if (!activeJobId || cancelling) return;
    setCancelling(true);
    try {
      await fetch(`${corridoApi("/cancel")}`, {
        method: "POST",
        headers: { "Content-Type": "application/json", ...authHeaders() },
        body: JSON.stringify({ jobId: activeJobId }),
      });
    } catch { /* clearing the UI matters more than the ack */ }
    stopPoll();
    clearActiveJob();
    setActiveJobId("");
    setProgress(0);
    setLiveStatus(null);
    setNote(t("corrido.cancelled"));
    setPhase("inputs");
    setCancelling(false);
    refreshUserCredits();
  };

  // ---- SHARED COMPLETION HANDLERS — used by the live flow AND the reload
  // reconnect (so a refresh mid-render lands in exactly the same place). ----
  const storyboardDone = (s: CorridoStatusResponse) => {
    clearActiveJob();
    setRunId(s.run_id);
    const board: Shot[] = s.storyboard || [];
    setShots(board);
    setVideoPartial(null);
    setRenderNotice(null);
    setApprovedIdxs(new Set()); // fresh storyboard — nothing approved yet
    setSelectedIdx(board[0]?.idx ?? null);
    setInputsOpen(false);
    setPhase("board");
    setNote(t("corrido.storyboardReady"));
  };
  /** An animation finished. `autoCut`: no clip review — a V2 job arrives
   *  with its video already cut (delivered here); a legacy one stops after
   *  animating and is cut at once from every clip that passed its check.
   *  Re-takes (autoCut false) land back on the review they came from. */
  const clipsDone = (s: CorridoStatusResponse, autoCut = true) => {
    clearActiveJob();
    const list: Clip[] = normalizeClips(s.clips);
    setClips(list);
    setVideoPartial(null); // every scene of this video came through
    setClipApproved(new Set()); // fresh takes — nothing approved yet
    setSelectedClipIdx(list[0]?.idx ?? null);
    const rid = s.run_id || runIdRef.current;
    if (s.run_id) setRunId((prev) => prev || s.run_id);
    setInputsOpen(false);
    if (autoCut && (s.result_url || (s.video && s.video.url))) { assembleDone(s); return; }
    // Cut from the clips that passed. When none did there is nothing worth
    // cutting unseen: the run stops on the clip review instead.
    const clean = list.filter((c) => !(c.qc && !c.qc.ok)).map((c) => c.idx);
    if (autoCut && clean.length && rid) {
      void assemble(clean, rid);
      return;
    }
    setPhase("clips");
    const flagged = list.filter(c => c.qc && !c.qc.ok).length;
    setNote(flagged
      ? t("corrido.clipsReadyFlagged", { count: flagged })
      : t("corrido.clipsReady"));
  };
  const assembleDone = (s: CorridoStatusResponse, revised = false) => {
    clearActiveJob();
    // "Ready" is only ever said with a file to play. A completed assemble with
    // no result_url used to show "Your video is ready" over an empty panel —
    // the product's end state, announced and never delivered. Now it stays on
    // the clips, says plainly that no file came back, and the customer can
    // build again.
    const url = s.result_url || (s.video && s.video.url) || "";
    if (!url) {
      setPhase(revised ? "done" : "clips");
      setNote(t("corrido.videoMissing"));
      refreshUserCredits();
      return;
    }
    try { localStorage.removeItem(CORRIDO_BOARD_KEY); } catch { /* video delivered — board save done its job */ }
    setVideoUrl(url);
    setPhase("done");
    setFixSelection({});
    setFixReasons({});
    setFixFocus(null);
    setNote(revised
      ? t("corrido.revisedDelivered")
      : t("corrido.videoReady"));
    publishStudioResult("corrido", {
      title: t(revised ? "corrido.resultTitleRevised" : "corrido.resultTitle", { song: songLabel || t("corrido.yourSong") }),
      url: imgUrl(url) + (revised ? "#rev" : ""),
    });
    refreshUserCredits();
  };
  /** A fix pass (POST /revise) is one job or two. Remove-only: the cut itself.
   *  With new takes: the re-takes first, then the cut the server chains after
   *  them (next_job_id), followed here so the pass always ends in the re-cut
   *  video. The re-takes alone are no video: read as one, the page said "no
   *  video came back" over the old cut, with the free pass spent, the new
   *  takes never cut in and the removed scene still in the video. */
  const reviseDone = (s: CorridoStatusResponse, rid?: string, exclude?: number[]) => {
    if (s.op !== "reroll_clips") { assembleDone(s, true); return; }
    const cutRun = s.run_id || rid || runIdRef.current;
    const removed = Array.isArray(s.excluded_idxs) ? s.excluded_idxs : (exclude || []);
    if (s.next_job_id) { followCut(s.next_job_id, cutRun, removed); return; }
    void startRevisionCut(cutRun, removed);
  };
  /** Poll the cut that ends a fix pass, saved so a reload picks it up. */
  const followCut = (jid: string, cutRun: string, exclude: number[]) => {
    saveActiveJob("revise", jid, cutRun, undefined, exclude);
    setPhase("revising");
    setNote(t("corrido.revisionCutting"));
    poll(jid, (s2) => reviseDone(s2, cutRun, exclude), undefined, reviseFailed);
  };
  /** The new takes are on the run but no cut followed them (the server could
   *  not start one, or its engine never chains one): start it from here — the
   *  run's clips minus the scenes marked remove, the same cut the server makes. */
  const startRevisionCut = async (cutRun: string, exclude: number[]) => {
    setPhase("revising");
    setNote(t("corrido.revisionCutting"));
    try {
      const r: CorridoMutationResponse = await fetch(corridoApi(exclude.length ? "/revise" : "/assemble"), {
        method: "POST", headers: authHeaders(),
        body: JSON.stringify(exclude.length ? { run_id: cutRun, exclude, reroll: [] } : { run_id: cutRun }),
      }).then(r => r.json());
      const jid = jobIdOf(r);
      if (!jid) { reviseFailed({}, errorText(t, r, "corrido.couldNotStartAssembly")); return; }
      followCut(jid, cutRun, exclude);
    } catch (e: unknown) { reviseFailed({}, getErrorMessage(e)); }
  };
  /** A fix pass that ended without a new video changed nothing the customer
   *  sees: the old video stays, and the pass is theirs to use again (the
   *  server still bounds the free re-takes). */
  const reviseFailed = (_s: CorridoStatusResponse, reason: string) => {
    clearActiveJob();
    setPhase("done");
    setRevisionUsed(false);
    setNote(reason);
  };
  /** A redraw finished: the status carries the whole board (36 entries) with
   *  the new still in place. Swap by idx — the customer's order is kept — and
   *  clear that scene's approval, because it is a new scene. */
  const applyRedrawnBoard = (board: Shot[] | undefined, idx: number, directed: boolean): boolean => {
    const fresh = Array.isArray(board) ? board : [];
    const byIdx = new Map(fresh.map((s) => [s.idx, s] as [number, Shot]));
    if (!byIdx.has(idx)) return false;
    const pos = Math.max(0, shotsRef.current.findIndex((s) => s.idx === idx)) + 1;
    setShots((prev) => (prev.length ? prev.map((s) => byIdx.get(s.idx) || s) : fresh));
    setApprovedIdxs((prev) => { const next = new Set(prev); next.delete(idx); return next; });
    setNote(t(directed ? "corrido.clipRegeneratedDirected" : "corrido.clipRegenerated", { n: pos }));
    return true;
  };

  // RECONNECT: a build that is running must never look like a fresh intro
  // (owner report 2026-09-28: the chat restarted at the intro while the board
  // said "still building"). Three ways in, one resume path:
  //   1. /?resume_corrido_job=<jobId> — the board's in-flight card was clicked;
  //   2. this browser's own active-job record (a reload mid-build);
  //   3. neither: ask /runs for the newest build in flight (another device,
  //      cleared storage) and pick that up.
  // Waits for sign-in: the ref is only spent once the token is known.
  const fetchJobStatus = async (jobId: string): Promise<CorridoStatusResponse> => {
    const jid = encodeURIComponent(jobId);
    const r = await fetch(`${corridoApi("/status")}?id=${jid}&job_id=${jid}`, { headers: authHeaders() });
    return (await r.json()) as CorridoStatusResponse;
  };

  /** The newest corrido build still running for this account, from /runs. */
  const findInFlightRun = async (): Promise<ActiveJobRecord | null> => {
    const r = await fetch(corridoApi("/runs"), { headers: authHeaders(false) });
    if (!r.ok) return null;
    const j = (await r.json()) as { runs?: Array<{ jobId?: string; type?: string; status?: string; run_id?: string | null; created_at?: string }> };
    const now = Date.now();
    const live = (Array.isArray(j?.runs) ? j.runs : [])
      .filter((row) => row && row.jobId && isInFlight(row.status) && kindFromRunType(row.type))
      // A row "processing" for longer than any render lasts is a dead job, not a build.
      .filter((row) => { const at = Date.parse(String(row.created_at || "")); return !Number.isFinite(at) || now - at <= CORRIDO_ACTIVE_JOB_TTL_MS; })
      .sort((a, b) => (Date.parse(String(b.created_at || "")) || 0) - (Date.parse(String(a.created_at || "")) || 0));
    const row = live[0];
    if (!row) return null;
    return { jobId: String(row.jobId), kind: kindFromRunType(row.type)!, runId: row.run_id || undefined, savedAt: now };
  };

  /** Pick one job back up: still running -> its live bar and poll; finished
   *  while away -> straight to the right review; stopped -> say why. */
  const resumeJob = async (found: ActiveJobRecord, known?: CorridoStatusResponse) => {
    const s = known ?? await fetchJobStatus(found.jobId);
    const rec: ActiveJobRecord = { ...found, kind: asRevise(found.kind, s) };
    // A fix pass picked back up has been used, whichever of its jobs this is
    // (one that already failed is the customer's to use again: reviseFailed).
    if (rec.kind === "revise" && (isInFlight(s.status) || s.status === "completed")) setRevisionUsed(true);
    if (isInFlight(s.status)) {
      if (rec.runId || s.run_id) setRunId(rec.runId || s.run_id);
      setInputsOpen(false);
      setNote(t("corrido.reconnected"));
      setProgress(s.progress || 0);
      if (rec.kind === "storyboard") {
        setPhase("working");
        // The board shows the building storyboard (and its bar) right away.
        publishBuildingBoard(s.run_id || rec.runId || "", Array.isArray(s.storyboard) ? s.storyboard : [], buildOf(rec.jobId, s));
        poll(rec.jobId, storyboardDone, (tick) => {
          if (tick.run_id) setRunId((prev) => prev || tick.run_id);
          publishBuildingBoard(tick.run_id || rec.runId || "", Array.isArray(tick.storyboard) ? tick.storyboard : [], buildOf(rec.jobId, tick));
        }, undefined, s);
      } else if (rec.kind === "clips" || rec.kind === "reroll" || rec.kind === "replace") {
        setPhase("animating");
        poll(rec.jobId, (s2) => clipsDone(s2, rec.kind === "clips" && !reviewWanted(rec.runId)), undefined, (s2, reason) => videoStopped(s2, reason, "inputs"), s);
      } else if (rec.kind === "regen") {
        // A redraw in flight: reopen the board it belongs to, mark the tile,
        // and pick the poll back up. The board comes from the run's saved
        // storyboard; the redraw's own status carries the merged board.
        const rid = rec.runId || s.run_id || "";
        if (rid) {
          try {
            const sr = await fetch(`${corridoApi("/storyboard_state")}?run_id=${encodeURIComponent(rid)}`, { headers: authHeaders(false) });
            const sj: CorridoStoryboardStateResponse = await sr.json();
            const board: Shot[] = sj.storyboard || [];
            if (board.length) { setShots(board); setSelectedIdx(board[0]?.idx ?? null); }
          } catch { /* the poll's own board fills it in */ }
        }
        const idx = typeof rec.idx === "number" ? rec.idx : -1;
        setPhase("board");
        setRegenning(idx >= 0 ? idx : null);
        poll(rec.jobId, (s2) => {
          clearActiveJob();
          if (idx < 0 || !applyRedrawnBoard(s2.storyboard, idx, false)) {
            const board = Array.isArray(s2.storyboard) ? s2.storyboard : [];
            if (board.length) setShots(board);
            // Picked up without knowing which scene (another device): the whole
            // board came back, which is all there is to say.
            setNote(idx < 0 && board.length ? t("corrido.storyboardRestored") : t("corrido.regenNoImage"));
          }
          setRegenning(null);
        }, undefined, (_s2, reason) => {
          setRegenning(null); setPhase("board");
          setNote(t("corrido.regenFailed", { n: idx + 1, reason }));
        }, s);
      } else if (rec.kind === "revise") {
        if (rec.kind !== found.kind) saveActiveJob("revise", rec.jobId, rec.runId || s.run_id, undefined, rec.exclude);
        setPhase("revising");
        poll(rec.jobId, (s2) => reviseDone(s2, rec.runId || s.run_id, rec.exclude), undefined, reviseFailed, s);
      } else {
        setPhase("assembling");
        poll(rec.jobId, (s2) => assembleDone(s2), undefined, (_s2, reason) => {
          setPhase("clips"); setNote(reason);
        }, s);
      }
    } else if (s.status === "completed") {
      // finished while away — jump straight to the right review stage
      if (rec.kind === "storyboard") storyboardDone(s);
      else if (rec.kind === "clips" || rec.kind === "reroll" || rec.kind === "replace") clipsDone(s, rec.kind === "clips" && !reviewWanted(rec.runId));
      else if (rec.kind === "regen") {
        clearActiveJob();
        const board: Shot[] = s.storyboard || [];
        if (board.length) {
          setRunId(rec.runId || s.run_id || "");
          setShots(board); setApprovedIdxs(new Set()); setSelectedIdx(board[0]?.idx ?? null);
          setInputsOpen(false); setPhase("board"); setNote(t("corrido.storyboardRestored"));
        }
      }
      else if (rec.kind === "revise") reviseDone(s, rec.runId || s.run_id, rec.exclude);
      else assembleDone(s);
    } else {
      // Only this job's own record is dropped — never another job's.
      if (readActiveJob()?.jobId === rec.jobId) clearActiveJob();
      if (s.error || s.error_code) setNote(t("corrido.lastRenderStopped", { reason: errorText(t, s) }));
    }
  };

  // "Make one like this" on the Reel (features/reel): its style, and the
  // photos picked in its sheet, so nobody is asked for them twice.
  const reelRef = React.useRef(false);
  React.useEffect(() => {
    if (reelRef.current) return;
    reelRef.current = true;
    const params = new URLSearchParams(window.location.search);
    if (params.get("from") !== "reel") return;
    const asked = params.get("style");
    if (asked && STYLE_CARDS.some((s) => s.id === asked)) setStyle(asked);
    const handoff = takeReelHandoff("music");
    if (handoff?.files?.length) addPhotos(toFileList(handoff.files));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const reconnectRef = React.useRef(false);
  React.useEffect(() => {
    if (reconnectRef.current || !loggedIn) return;
    const params = new URLSearchParams(window.location.search);
    if (params.get("resume_corrido")) return; // the deliberate run resume owns this load
    reconnectRef.current = true;
    const jobParam = params.get("resume_corrido_job");
    (async () => {
      try {
        if (jobParam) {
          let s: CorridoStatusResponse;
          try { s = await fetchJobStatus(jobParam); }
          finally {
            // drop the param so a later refresh of the page doesn't re-trigger
            window.history.replaceState(window.history.state, "", window.location.pathname + "#director-console");
          }
          const local = readActiveJob();
          const mine = local && local.jobId === jobParam ? local : null;
          const kind: ActiveJobKind = (mine?.kind === "revise" || mine?.kind === "replace" ? mine.kind : null)
            || kindFromOp(s.op) || mine?.kind || kindFromPayload(s);
          const rec: ActiveJobRecord = { jobId: jobParam, kind: asRevise(kind, s), runId: s.run_id || mine?.runId, idx: mine?.idx, exclude: mine?.exclude, savedAt: Date.now() };
          if (isInFlight(s.status)) saveActiveJob(rec.kind, rec.jobId, rec.runId, rec.idx, rec.exclude);
          await resumeJob(rec, s);
          return;
        }
        const local = readActiveJob();
        if (local) { await resumeJob(local); return; }
        const found = await findInFlightRun();
        // The customer may have started something while /runs was answering:
        // their new build wins, the found one stays on the board as a card.
        if (!found || readActiveJob() || pollTimer.current || (phaseRef.current !== "mode" && phaseRef.current !== "inputs")) return;
        saveActiveJob(found.kind, found.jobId, found.runId);
        await resumeJob(found);
      } catch { /* status unavailable — the /runs cards still cover recovery */ }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loggedIn]);

  // Publish a live "building" board so the storyboard is visible on the board the
  // instant the build starts — placeholder tiles that fill with real stills as
  // they stream back from the pipeline (partial `s.storyboard` on each poll).
  // `build` carries the live numbers so the board draws the same moving bar.
  const publishBuildingBoard = React.useCallback((rid: string, real: Shot[], build: StoryboardBuild | null = null) => {
    const clips = buildSkeletonClips(t, rid, !!artistMode, real, scenesPlan ? stylePlan!.count : 4);
    buildingBoardUp.current = true;
    publishStoryboardProject({
      runId: rid, service: "corrido", clips,
      totalDurationSec: clips.reduce((sum, c) => sum + c.durationSec, 0),
      selectedIdx: clips[0]?.idx ?? null,
      songName: songLabel, styleId: style, styleLabel: styleLabelFor(t, style),
      beats: beats.trim() || null, renderCostCredits: renderCost, creditBalance: balance,
      phase: "working",
      photos: buildPhotos.map((p, i) => ({ name: p.name, url: photoPreviews[i] ?? "", ok: null as boolean | null, reason: null })),
      build,
    });
  }, [artistMode, songLabel, style, beats, renderCost, balance, buildPhotos, photoPreviews, scenesPlan, stylePlan]);

  const addPhotos = (list: FileList | null) => {
    // Copy IMMEDIATELY — FileList is live, and clearing the input (so the same
    // file can be re-picked) empties it before React's async state update runs.
    const picked = Array.from(list || []);
    if (!picked.length) return;
    const unsupported = picked.filter((file) => !isAcceptedCorridoPhoto(file));
    if (unsupported.length) {
      setNote(t("corrido.unsupportedPhoto", { name: unsupported[0].name }));
    }
    setPhotos(prev => {
      const seen = new Set(prev.map(f => `${f.name}_${f.size}`));
      const next = [...prev, ...picked.filter((f) => isAcceptedCorridoPhoto(f) && !seen.has(`${f.name}_${f.size}`))];
      // The engine refuses 7+ (E_SELFIE_COUNT); cap here and say so, so the
      // customer is never told "too many" only after the upload.
      if (next.length > MAX_ARTIST_PHOTOS) {
        setNote(t("corrido.photosMaxReached"));
        return next.slice(0, MAX_ARTIST_PHOTOS);
      }
      return next;
    });
  };

  /** The song's upload, once per file: a build waiting for its avatar uploads
   *  it early (so the choice survives leaving the page) and the build reuses it. */
  const ensureSongUrl = React.useCallback((file: File): Promise<string> => {
    const cur = songUpload.current;
    if (cur.file === file && cur.url) return Promise.resolve(cur.url);
    if (cur.file === file && cur.promise) return cur.promise;
    const promise = uploadFile(t, file, "audio").then((url) => {
      if (songUpload.current.file === file) songUpload.current = { file, url, promise: null };
      return url;
    });
    songUpload.current = { file, url: "", promise };
    promise.catch(() => { if (songUpload.current.promise === promise) songUpload.current = { file: null, url: "", promise: null }; });
    return promise;
  }, [t]);

  /** Stop waiting for an avatar: the build started, or the customer went another way. */
  const stopFaceLockWait = () => {
    clearFaceLockWait("corrido");
    setWaitingFaceLockId(null);
    waitArmed.current = false;
  };

  /** A new avatar was just created in the avatar step: keep the choices and wait for it. */
  const onFaceLockCreated = (avatar: FaceLockAvatar | null) => {
    void refreshCrew();
    if (!avatar) return;
    setFailedFaceLockId(null);
    setFaceLockId(avatar.id);
    setWaitingFaceLockId(avatar.id);
    waitArmed.current = true;
  };

  // A build waiting for its avatar keeps its choices (song, genre, words) in
  // this browser, the song as its uploaded URL, so leaving and coming back
  // loses nothing (the same never-lose rule as the in-flight job record).
  React.useEffect(() => {
    if (!waitingFaceLockId) return;
    let alive = true;
    const base: CorridoWaitChoices = { style, beats, ...(savedSong ? { songUrl: savedSong.url, songName: savedSong.name } : {}) };
    saveFaceLockWait("corrido", waitingFaceLockId, base);
    if (song) {
      ensureSongUrl(song).then((url) => {
        if (alive) saveFaceLockWait("corrido", waitingFaceLockId, { ...base, songUrl: url, songName: song.name });
      }).catch(() => { /* the build uploads it again; only a return visit would need the song re-added */ });
    }
    return () => { alive = false; };
  }, [waitingFaceLockId, style, beats, song, savedSong, ensureSongUrl]);

  // Coming back (or reloading) while a build waited for its avatar: the choices
  // come back with that avatar selected. Still training -> it carries on by
  // itself when ready; ready already -> selected, and the customer builds.
  const waitRestoreRef = React.useRef(false);
  React.useEffect(() => {
    if (waitRestoreRef.current || !loggedIn || (crewStatus !== "on" && crewStatus !== "off")) return;
    waitRestoreRef.current = true;
    const rec = readFaceLockWait<CorridoWaitChoices>("corrido");
    if (!rec) return;
    if (crewStatus === "off" || !crew?.required) { clearFaceLockWait("corrido"); return; }
    // A deliberate resume or a build already on screen owns this load.
    if (new URLSearchParams(window.location.search).get("resume_corrido") || phaseRef.current !== "mode") return;
    const c = rec.choices || {};
    setArtistMode(true);
    setPhase("inputs");
    if (typeof c.style === "string" && STYLE_CARDS.some((s) => s.id === c.style)) setStyle(c.style);
    if (typeof c.beats === "string") setBeats(c.beats);
    if (typeof c.songUrl === "string" && c.songUrl) setSavedSong({ url: c.songUrl, name: c.songName || t("corrido.yourSong") });
    setFaceLockId(rec.avatarId);
    setWaitingFaceLockId(rec.avatarId);
    waitArmed.current = false;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loggedIn, crewStatus, crew]);

  // The awaited avatar, as each read of the crew reports it.
  React.useEffect(() => {
    if (!waitingFaceLockId || !crew) return;
    const a = crew.avatars.find((x) => x.id === waitingFaceLockId);
    if (!a) return; // just created: the next read lists it
    if (a.castable || isStillsOnly(a)) {
      const armed = waitArmed.current;
      setWaitingFaceLockId(null);
      waitArmed.current = false;
      setFaceLockId(a.id);
      if (!armed) { setNote(t("faceLock.required.corridoResumed", { name: a.name })); return; }
      if (phaseRef.current === "inputs" && artistModeRef.current === true && (song || savedSong)) {
        setNote(t("faceLock.required.corridoStarting", { name: a.name }));
        void startRef.current(a.id);
      } else {
        setNote(t("faceLock.required.corridoReadyAddSong", { name: a.name }));
      }
      return;
    }
    if (a.status === "failed") {
      stopFaceLockWait();
      setFaceLockId(null);
      setFailedFaceLockId(a.id);
      return;
    }
    // Training while this page is open: carry on by itself once it is ready.
    waitArmed.current = true;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [crew, waitingFaceLockId]);

  /** `castId`: start with this avatar (the auto-continue once it is ready). */
  const start = async (castId?: string) => {
    const cast = castId ? castableFaceLocks.find((a) => a.id === castId) || null : (usingFaceLock ? chosenFaceLock : null);
    if (!song && !savedSong) { setNote(t("corrido.addSongFirst")); return; }
    if (artistMode && faceLockRequired && !cast) { setNote(t("faceLock.required.corridoNeedAvatar")); return; }
    if (artistMode && !cast && (photos.length < MIN_ARTIST_PHOTOS || photos.length > MAX_ARTIST_PHOTOS)) { setNote(t("corrido.needCloseups")); return; }
    try { localStorage.removeItem(CORRIDO_BOARD_KEY); } catch { /* fresh build supersedes any recovered board */ }
    clearActiveJob();   // a fresh build supersedes any previous job record
    stopFaceLockWait(); // …and any build that was waiting for its avatar
    setFailedFaceLockId(null);
    setPhase("working"); setNote(t("corrido.uploadingFiles")); setProgress(1); setLiveStatus(null);
    // Show the storyboard board immediately — placeholder tiles that fill in as
    // stills render, so the board is never a lone progress bar.
    publishBuildingBoard("", [], buildOf("", null));
    try {
      // A song already uploaded while the build waited for its avatar is reused.
      const songUrl = song
        ? (songUpload.current.file === song && songUpload.current.url ? songUpload.current.url : await uploadFile(t, song, "audio"))
        : savedSong!.url;
      const photoUrls: string[] = [];
      // A chosen FaceLock carries its own close-ups: nothing to upload or check.
      const sendPhotos = artistMode && !cast ? photos : NO_PHOTOS;
      for (const p of sendPhotos) {
        try {
          const normalized = await normalizeCorridoPhotoForUpload(p);
          photoUrls.push(await uploadFile(t, normalized, "image"));
        } catch {
          throw new Error(t("corrido.photoPrepFailed", { name: p.name }));
        }
      }

      // Photo check — artist mode only (faceless sends no photos and skips it).
      // The current engine checks the photos INSIDE /storyboard and answers
      // this route with {ok:true, skipped:true} — or does not serve it at all
      // (404/501). Both mean "go on"; only a real verdict can stop the build.
      if (artistMode && photoUrls.length) {
        setNote(t("corrido.checkingPhotos"));
        const vres = await fetch(corridoApi("/validate"), {
          method: "POST", headers: authHeaders(), body: JSON.stringify({ photos: photoUrls }),
        });
        const vr: CorridoValidateResponse = (vres.status === 404 || vres.status === 501)
          ? { ok: true, skipped: true }
          : await vres.json().catch(() => ({} as CorridoValidateResponse));
        const skipped = vr.skipped === true || (vr.ok === true && !Array.isArray(vr.results) && vr.ready === undefined);
        if (!skipped) {
          // per-photo verdicts: results come back in the same order the photos were sent
          const perPhoto = (vr.results || []).map((entry, i: number) => {
            const r = entry?.[1] || {};
            return { name: sendPhotos[i]?.name || t("corrido.photoOrdinal", { n: i + 1 }), ok: r.status === "accept", reason: r.reason || "" };
          });
          setVerdicts(perPhoto);
          if (!vr.ready) {
            setPhase("inputs");
            // Three distinct failures, three distinct messages:
            // render service offline (our machine), upload storage unreadable
            // (our backend), or actual photo-quality rejections (user's fix).
            setNote(vr.error === "render_service_down" ? `${vr.message || t("corrido.engineOffline")}`
              : vr.error === "asset_download_failed" ? `${vr.message || t("corrido.assetDownloadFailed")} ${t("corrido.assetDownloadFailedTail")}`
                : `${vr.message || t("corrido.needClearerPhotos")} ${t("corrido.needClearerPhotosTail")}`);
            return;
          }
          if (vr.message) setNote(`${vr.message}`);
        }
      }

      setNote(t("corrido.buildingStoryboard"));
      // The storyboard charges nothing; the video price is taken at animate.
      const gen: CorridoMutationResponse = await fetch(corridoApi("/storyboard"), {
        method: "POST", headers: authHeaders(),
        body: JSON.stringify({
          song_url: songUrl,
          ref_photos: artistMode ? photoUrls : [],
          // The server swaps in the FaceLock's own close-ups and its trained face.
          ...(artistMode && cast ? { avatar_id: cast.id } : {}),
          style,
          mode: artistMode ? "artist" : "faceless",
          beats: artistMode ? (beats.trim() || undefined) : undefined,
        }),
      }).then(r => r.json());
      const genId = jobIdOf(gen);
      if (!genId) {
        setPhase("inputs");
        setNote(errorText(t, gen, "corrido.couldNotStart"));
        // Refused for want of an avatar (the rule switched on since this page
        // loaded): read the crew again so the avatar step shows.
        if (gen && gen.error === "facelock_required") void refreshCrew();
        return;
      }
      saveActiveJob("storyboard", genId);   // a reload reconnects to this build
      publishBuildingBoard("", [], buildOf(genId, null));
      poll(genId, storyboardDone, (s) => {
        // Progressive fill: capture the run id early (so recovery works mid-build)
        // and stream whatever stills exist so far onto the placeholder board,
        // with the live numbers for the board's bar.
        if (s.run_id) setRunId((prev) => prev || s.run_id);
        publishBuildingBoard(s.run_id || runId || "", Array.isArray(s.storyboard) ? s.storyboard : [], buildOf(genId, s));
      });
    } catch (e: unknown) { setPhase("inputs"); setNote(getErrorMessage(e)); }
  };

  // Regenerate one scene; `scene` (optional) steers it toward a story beat —
  // the engine replans that single shot around the note.
  //
  // /regen answers {ok:true, job_id} for a job that runs on the GPU for a
  // while; the new still only exists when /status says `completed`, and that
  // status carries the whole storyboard with the new image in place. Reading
  // the answer synchronously (the old code) meant the tile snapped back to the
  // old image every time while the GPU spent real money on a still nobody saw.
  // The tile shows "redrawing…" meanwhile; on failure the old still stays and
  // the reason is said in plain words.
  const regen = async (idx: number, scene?: string, reasons?: string[]) => {
    const directed = !!scene?.trim();
    const pos = Math.max(0, shots.findIndex((s) => s.idx === idx)) + 1;
    // The "what's wrong" chips only teach: sent when picked, never read by the redraw.
    const why = cleanReasons(reasons, "stills");
    setRegenning(idx);
    setNote(t("corrido.redrawing", { n: pos }));
    try {
      const r: CorridoMutationResponse = await fetch(corridoApi("/regen"), {
        method: "POST", headers: authHeaders(),
        body: JSON.stringify({ run_id: runId, idx, scene: scene?.trim() || undefined, ...(why.length ? { reasons: why } : {}) }),
      }).then(r => r.json());
      // "With your direction" only when the typed words reached the redraw.
      const shown = directed && r.steered !== false;
      // An engine that answers with the board straight away (no job) is still honoured.
      if (Array.isArray(r.storyboard) && r.storyboard.length && !jobIdOf(r)) {
        if (!applyRedrawnBoard(r.storyboard, idx, shown)) setNote(t("corrido.regenNoImage"));
        setRegenning(null);
        return;
      }
      const jid = jobIdOf(r);
      if (!jid) { setNote(errorText(t, r, "corrido.couldNotStartRegen")); setRegenning(null); return; }
      saveActiveJob("regen", jid, runId, idx);
      poll(jid, (s) => {
        clearActiveJob();
        if (!applyRedrawnBoard(s.storyboard, idx, shown)) setNote(t("corrido.regenNoImage"));
        setRegenning(null);
      }, undefined, (_s, reason) => {
        // The old tile was never touched; just say why the new one did not come.
        setRegenning(null);
        setPhase("board");
        setNote(t("corrido.regenFailed", { n: pos, reason }));
      });
    } catch (e: unknown) { setNote(t("corrido.regenFailed", { n: pos, reason: getErrorMessage(e) })); setRegenning(null); }
  };


  // STAGE 2 — animate every approved still (the charged, GPU-heavy step) and
  // cut the video, with no second approval: each clip is checked on the worker
  // as it is made, and the delivered video keeps its free fix pass (clipsDone).
  const startClips = async () => {
    setConfirmingRender(false);
    setContinueAsk(false);
    setRenderNotice(null);
    // Where a failure returns to: the board, or the clip review when this run
    // was reopened with its kept clips and no board on screen.
    const back: Phase = shots.length ? "board" : "clips";
    setPhase("animating"); setProgress(1); setLiveStatus(null);
    setNote(t(reviewClipsRef.current ? "corrido.animatingClipsReview" : "corrido.animatingClips"));
    try {
      // shot_order is the customer's arrangement and the engine honours it.
      // No clip lengths: the engine cuts to the song and read nothing else.
      // A continuation with no board on screen names no order: the engine
      // keeps the order of the attempt it continues.
      const r: CorridoMutationResponse = await fetch(corridoApi("/render_clips"), {
        method: "POST", headers: authHeaders(),
        body: JSON.stringify({
          run_id: runId,
          ...(shots.length ? { shot_order: shots.map(s => s.idx) } : {}),
          ...(reviewClipsRef.current ? { review_clips: true } : {}),
          // The site mark on the finished video (lib/site-mark.ts): on unless unticked.
          watermark: keepSiteMark(),
        }),
      }).then(r => r.json());
      const jid = jobIdOf(r);
      if (!jid) {
        // A refused start goes back to the board AND says why there.
        const why = r.error === "insufficient_credits" ? t("corrido.insufficientCredits") : errorText(t, r, "corrido.couldNotStartRender");
        setPhase(back);
        setNote(why); setRenderNotice(why);
        return;
      }
      refreshUserCredits(); // animation is the step that charges credits
      // NOTE: the local board save is deliberately KEPT until the video is
      // delivered (assembleDone clears it) — a failed/interrupted stage-2 must
      // still be able to reopen the approved stills board.
      saveActiveJob("clips", jid, runId);
      rememberReview(runId, reviewClipsRef.current);
      publishStudioResult("corrido", null); // a new render supersedes the last player node
      // A failed animation returns the customer to the SAME approved board —
      // the storyboard is theirs. Scenes already made are kept (E_VIDEO_PARTIAL)
      // and "Continue" renders only the rest.
      const review = reviewClipsRef.current;
      poll(jid, (s) => clipsDone(s, !review), undefined, (s, reason) => videoStopped(s, reason, back));
    } catch (e: unknown) { setPhase(back); setNote(getErrorMessage(e)); setRenderNotice(getErrorMessage(e)); }
  };

  // "Confirm & run", on the board and in the chat. It counts only while the
  // price is on screen for a board that is still ready (every scene approved,
  // nothing being redrawn), and not in the half second after the question went
  // up: a stray event, a second Enter or a held key never starts the charged
  // step. A short balance stays on the question, which already says to top up.
  const confirmRun = () => {
    if (phase !== "board" || !confirmingRender) return;
    if (regenning !== null || !allApproved) { setConfirmingRender(false); return; }
    if (Date.now() - confirmAskedAt.current < 500) return;
    if (balance !== null && balance < renderCost) return;
    trackEvent("corrido_render_confirmed", { credits: renderCost });
    void startClips();
  };

  // THE PAY SHEET KEEPS THIS RUN (Studio Blueprint 04): checkout comes back
  // with ?resume_corrido=<run>, which reopens the board above. The server
  // does not keep approvals, so the order the buyer left and their review
  // choice ride along; every scene was approved before they could pay, so
  // all are approved again, and the render starts once this flow's balance
  // shows the new credits. A cancel or a payment still clearing leaves the
  // board approved and waiting.
  usePayJob("music", () => (runId
    ? { runId, payload: phase === "board" ? { order: shots.map((sh) => sh.idx), review: reviewClips } : undefined }
    : null));
  const [pendingRunId] = React.useState(() => readPendingJob()?.runId || "");
  const [resumeRender, setResumeRender] = React.useState(false);
  const reopenPaidBoard = (job: { payload?: unknown }) => {
    const p = (job.payload || {}) as { order?: number[]; review?: boolean };
    if (!Array.isArray(p.order)) return false;
    if (p.order.length === shots.length) {
      const byIdx = new Map(shots.map((sh) => [sh.idx, sh]));
      const ordered = p.order.map((i) => byIdx.get(i)).filter((sh): sh is Shot => !!sh);
      if (ordered.length === shots.length) setShots(ordered);
    }
    setApprovedIdxs(new Set(shots.map((sh) => sh.idx)));
    if (typeof p.review === "boolean") setReviewClips(p.review);
    return true;
  };
  useResumeJob("music", loggedIn && phase === "board" && !!runId && runId === pendingRunId && shots.length > 0 && regenning === null, {
    start: (job) => { if (reopenPaidBoard(job)) setResumeRender(true); },
    restore: (job) => { reopenPaidBoard(job); },
  });
  React.useEffect(() => {
    if (!resumeRender || phase !== "board" || !allApproved || balance === null || balance < renderCost) return;
    setResumeRender(false);
    trackEvent("corrido_render_confirmed", { credits: renderCost, resumed: true });
    void startClips();
    // startClips reads this render's state; it runs once per paid return.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resumeRender, phase, allApproved, balance, renderCost]);

  const dismissRender = () => {
    trackEvent("corrido_render_confirm_dismissed", { credits: renderCost });
    setConfirmingRender(false);
  };

  // "Continue my video" in the chat. Free while the first charge stands;
  // otherwise it costs a video, so the first press asks and the second starts.
  const continueVideo = () => {
    if (!videoPartial) return;
    if (videoPartial.free) { void startClips(); return; }
    if (!continueAsk) { setContinueAsk(true); return; }
    if (balance !== null && balance < renderCost) return;
    void startClips();
  };

  // ---- OWNER: recover a video that was lost before it was delivered ----
  // Look first (nothing is published), then bring the scenes back. Admin
  // accounts only; the server refuses anyone else.
  const recoverPost = async (dryRun: boolean): Promise<CorridoMutationResponse> =>
    fetch(corridoApi("/v2/admin/recover"), {
      method: "POST", headers: authHeaders(),
      body: JSON.stringify({ run_id: runId, dry_run: dryRun }),
    }).then(r => r.json());
  const recoverScan = async () => {
    if (!runId || recover?.busy) return;
    setRecover({ busy: true });
    try {
      const r = await recoverPost(true);
      const jid = jobIdOf(r);
      if (!jid) { setRecover({ busy: false, note: t("corrido.recoverFailed", { reason: r.message || r.error || "" }) }); return; }
      // A look takes a few minutes (the studio has to start). Stop asking after 30.
      for (let i = 0; i < 360; i += 1) {
        await new Promise((res) => setTimeout(res, 5000));
        if (!mountedRef.current) return;
        let st: CorridoRecoverStatus;
        try {
          st = await fetch(`${corridoApi("/v2/admin/recover/status")}?id=${encodeURIComponent(jid)}`, { headers: authHeaders(false) }).then(x => x.json());
        } catch { continue; }
        if (st.status === "completed") {
          const found = Number(st.report?.found || 0);
          const expected = Number(st.report?.expected || 0);
          setRecover({ busy: false, found, expected, note: found ? t("corrido.recoverFound", { found, expected }) : t("corrido.recoverNone") });
          return;
        }
        if (st.status === "failed" || st.status === "cancelled") {
          setRecover({ busy: false, note: t("corrido.recoverFailed", { reason: st.error || st.message || "" }) });
          return;
        }
      }
      setRecover({ busy: false, note: t("corrido.recoverFailed", { reason: t("corrido.lostContact") }) });
    } catch (e: unknown) { setRecover({ busy: false, note: t("corrido.recoverFailed", { reason: getErrorMessage(e) }) }); }
  };
  const recoverNow = async () => {
    if (!runId || recover?.busy) return;
    const back: Phase = shots.length ? "board" : "clips";
    setRecover({ ...(recover || {}), busy: true });
    try {
      const r = await recoverPost(false);
      const jid = jobIdOf(r);
      if (!jid) { setRecover({ busy: false, note: t("corrido.recoverFailed", { reason: r.message || r.error || "" }) }); return; }
      setRecover(null);
      setConfirmingRender(false); setRenderNotice(null);
      setPhase("animating"); setProgress(1); setLiveStatus(null);
      setNote(t("corrido.recoverRunning"));
      saveActiveJob("clips", jid, runId);
      poll(jid, clipsDone, undefined, (s, reason) => videoStopped(s, reason, back));
    } catch (e: unknown) { setRecover({ busy: false, note: t("corrido.recoverFailed", { reason: getErrorMessage(e) }) }); }
  };

  // One fresh take of one animated clip (free; ~10-15 min).
  const rerollClip = async (idx: number, reasons?: string[]) => {
    if (rerollsUsed >= MAX_REROLLS) { setNote(t("corrido.rerollLimit", { max: MAX_REROLLS })); return; }
    const why = cleanReasons(reasons, "clips");
    setRerolling(idx);
    setNote(t("corrido.newTakeRendering"));
    try {
      const r: CorridoMutationResponse = await fetch(corridoApi("/reroll"), {
        method: "POST", headers: authHeaders(),
        body: JSON.stringify({ run_id: runId, idx, ...(why.length ? { reasons: why } : {}) }),
      }).then(r => r.json());
      const jid = jobIdOf(r);
      if (!jid) { setNote(errorText(t, r, "corrido.couldNotStartReroll")); setRerolling(null); return; }
      setRerollsUsed(n => n + 1);
      saveActiveJob("reroll", jid, runId);
      poll(jid, (s) => {
        clearActiveJob();
        const fresh = normalizeClips(s.clips);
        if (fresh.length) setClips(fresh);
        setClipApproved(prev => { const next = new Set(prev); next.delete(idx); return next; });
        setRerolling(null);
        setNote(t("corrido.newTakeReady"));
      }, undefined, (_s, reason) => {
        // The clips are still there to review; only this take did not come.
        setRerolling(null); setPhase("clips"); setNote(reason);
      });
    } catch (e: unknown) { setNote(getErrorMessage(e)); setRerolling(null); }
  };

  // Replace the SCENE of one clip: brand-new still (optionally steered), then
  // animated. Re-roll re-takes the SAME scene — if the scene itself is bad,
  // this is the button that actually fixes it.
  const replaceClip = async (idx: number, scene?: string, reasons?: string[]) => {
    if (rerollsUsed >= MAX_REROLLS) { setNote(t("corrido.rerollLimit", { max: MAX_REROLLS })); return; }
    const why = cleanReasons(reasons, "clips");
    setRerolling(idx);
    setNote(t("corrido.buildingNewScene"));
    try {
      const r: CorridoMutationResponse = await fetch(corridoApi("/replace"), {
        method: "POST", headers: authHeaders(),
        body: JSON.stringify({ run_id: runId, idx, scene: scene?.trim() || null, ...(why.length ? { reasons: why } : {}) }),
      }).then(r => r.json());
      const jid = jobIdOf(r);
      if (!jid) { setNote(errorText(t, r, "corrido.couldNotStartReplace")); setRerolling(null); return; }
      setRerollsUsed(n => n + 1);
      saveActiveJob("replace", jid, runId);
      poll(jid, (s) => {
        clearActiveJob();
        const fresh = normalizeClips(s.clips);
        if (fresh.length) setClips(fresh);
        setClipApproved(prev => { const next = new Set(prev); next.delete(idx); return next; });
        setRerolling(null);
        setNote(t("corrido.newSceneReady"));
      }, undefined, (_s, reason) => {
        setRerolling(null); setPhase("clips"); setNote(reason);
      });
    } catch (e: unknown) { setNote(getErrorMessage(e)); setRerolling(null); }
  };

  // STAGE 2 FINISH — assemble ONLY the approved clips (free, fast-ish).
  // /assemble answers {ok:true, job_id} for a NEW job; the mp4 exists only when
  // /status for THAT id says completed with a result_url. Nothing is announced
  // before then (assembleDone).
  const assemble = async (pick?: number[], forRun?: string) => {
    const approved = pick ?? clips.filter(c => clipApproved.has(c.idx)).map(c => c.idx);
    const cutRun = forRun || runId;
    setCutFromReview(pick === undefined);
    // The cut plays in the order the customer arranged on the BOARD, not in
    // engine index order. When the board is not in memory (a resume that
    // landed straight on the clips) the server uses the order it animated in.
    const kept = new Set(approved);
    const boardOrder = shots.length ? shots.map(s => s.idx).filter(i => kept.has(i)) : null;
    setPhase("assembling"); setProgress(1); setLiveStatus(null);
    setNote(t("corrido.assembling"));
    try {
      const r: CorridoMutationResponse = await fetch(corridoApi("/assemble"), {
        method: "POST", headers: authHeaders(),
        // `review` only teaches the learning loop: true when the customer
        // picked these clips on the review (the ones left out were turned
        // down), false for this page's own cut of the clips that passed.
        body: JSON.stringify({ run_id: cutRun, approved_idxs: approved, review: pick === undefined,
          ...(boardOrder && boardOrder.length === approved.length ? { shot_order: boardOrder } : {}) }),
      }).then(r => r.json());
      const jid = jobIdOf(r);
      if (!jid) { setPhase("clips"); setNote(errorText(t, r, "corrido.couldNotStartAssembly")); return; }
      saveActiveJob("assemble", jid, cutRun);
      poll(jid, (s) => assembleDone(s), undefined, (_s, reason) => { setPhase("clips"); setNote(reason); });
    } catch (e: unknown) { setPhase("clips"); setNote(getErrorMessage(e)); }
  };


  // THE EMERGENCY PLAN, productized: one free revision pass on the delivered
  // video — drop bad clips (CPU-minutes) and/or re-roll them (~12 min each),
  // then re-assemble. No refunds, no 6-hour redo.
  const revise = async () => {
    const exclude = Object.entries(fixSelection).filter(([, v]) => v === "exclude").map(([k]) => Number(k));
    const reroll = Object.entries(fixSelection).filter(([, v]) => v === "reroll").map(([k]) => Number(k));
    if (!exclude.length && !reroll.length) { setNote(t("corrido.markOneClip")); return; }
    // Chips picked on a clip that is still marked, by its idx; none -> no key.
    const reasons: Record<string, string[]> = {};
    for (const idx of [...exclude, ...reroll]) {
      const why = cleanReasons(fixReasons[idx], "clips");
      if (why.length) reasons[String(idx)] = why;
    }
    setPhase("revising"); setProgress(1); setLiveStatus(null); setFixMode(false);
    setNote(t("corrido.revising", { count: reroll.length, takes: reroll.length, removed: exclude.length }));
    try {
      const r: CorridoMutationResponse = await fetch(corridoApi("/revise"), {
        method: "POST", headers: authHeaders(),
        body: JSON.stringify({ run_id: runId, exclude, reroll, ...(Object.keys(reasons).length ? { reasons } : {}) }),
      }).then(r => r.json());
      const jid = jobIdOf(r);
      if (!jid) { setPhase("done"); setNote(errorText(t, r, "corrido.couldNotStartRevision")); return; }
      setRevisionUsed(true);
      saveActiveJob("revise", jid, runId, undefined, exclude);
      poll(jid, (s) => reviseDone(s, runId, exclude), undefined, reviseFailed);
    } catch (e: unknown) { setPhase("done"); setNote(getErrorMessage(e)); }
  };

  // Keep the board-handler refs pointed at the latest closures/values.
  regenRef.current = regen;
  startClipsRef.current = startClips;
  confirmRunRef.current = confirmRun;
  dismissRenderRef.current = dismissRender;
  startRef.current = start;
  artistModeRef.current = artistMode;
  regenningRef.current = regenning;
  allApprovedRef.current = allApproved;
  shotsRef.current = shots;
  runIdRef.current = runId;
  rerollClipRef.current = rerollClip;
  replaceClipRef.current = replaceClip;
  assembleRef.current = assemble;
  rerollingRef.current = rerolling;
  phaseRef.current = phase;

  if (!loggedIn) {
    return (
      <div className="text-[15px] leading-[1.55] sm:text-sm">
        <p className="mb-3">{t("corrido.signInPrompt")}</p>
        {/* Back to this stop after signing in: a bare / opens on the welcome
            cards now (2026-09-24), not the studio this link was pressed in;
            #director-console opens the console itself in view, not the
            pitch 3,000px above it (2026-10-05 walk).
            44px tall and 15px on a phone (was 34px): it is the only way on.
            Full width below sm like the pitch CTAs, and balanced if a long
            label (Spanish at 320px) has to take two lines. 4px sides
            below 360px (2026-09-25) keep "Registrarse / Iniciar sesión →"
            on one line at 320. */}
        <a href="/signup?redirect_to=%2F%3Ftool%3Dcorrido%23director-console" className="inline-flex min-h-[44px] w-full items-center justify-center text-balance rounded-md bg-primary px-5 py-2.5 text-center text-[15px] font-semibold text-primary-foreground active:scale-[0.98] max-[359px]:px-1 sm:w-auto sm:text-sm">{t("corrido.signUpLogIn")}</a>
      </div>
    );
  }


  return (
    <div data-testid="corrido-studio" style={{ display: "flex", flexDirection: "column", gap: 10, fontFamily: "system-ui, sans-serif", fontSize: 13, color: C.offwhite }}>
      {/* studio header: name + credits, always visible */}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10, flexWrap: "wrap" }}>
        <div style={{ fontFamily: "'Rajdhani', sans-serif", fontSize: 13, fontWeight: 700, letterSpacing: 3, background: `linear-gradient(90deg,${C.cyan},${C.pink})`, WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent" }}>{t("corrido.studioTitle")}
        </div>
        <CreditsChip balance={balance} />
      </div>

      {/* NOTE: the resume banner used to live here and cost the user an
          in-progress build with one accidental click (field test 2026-07-17).
          In-flight and finished renders now belong in the ChopShop gallery. */}

      {/* mode: first the look (Realism or FaceShot Animation, for an account
          the 3D product is open to), then with the artist or without */}
      {phase === "mode" && <CorridoLookChoice />}
      {phase === "mode" && (
        <div style={{ display: "flex", gap: 8 }}>
          <button onClick={() => { setArtistMode(true); setPhase("inputs"); }}
            style={{ flex: 1, borderRadius: 10, border: `1px solid ${C.border}`, background: C.surface, color: C.offwhite, padding: "12px 14px", textAlign: "left", cursor: "pointer" }}>
            <b style={{ color: C.white, fontSize: 14 }}>{t("corrido.modeArtistTitle")}</b><br /><span style={{ fontSize: 13, color: C.muted }}>{t(faceLockRequired ? "faceLock.required.corridoModeDesc" : "corrido.modeArtistDesc")}</span>
          </button>
          <button onClick={() => { setArtistMode(false); setPhase("inputs"); }}
            style={{ flex: 1, borderRadius: 10, border: `1px solid ${C.border}`, background: C.surface, color: C.offwhite, padding: "12px 14px", textAlign: "left", cursor: "pointer" }}>
            <b style={{ color: C.white, fontSize: 14 }}>{t("corrido.modeFacelessTitle")}</b><br /><span style={{ fontSize: 13, color: C.muted }}>{t("corrido.modeFacelessDesc")}</span>
          </button>
        </div>
      )}

      {/* INPUT ASSETS panel */}
      {(phase === "inputs" || (phase !== "mode" && inputsOpen)) && (
        <div style={{ background: C.card, border: `1px solid ${C.borderHi}`, borderRadius: 12, padding: 12 }}>
          <PanelLabel>{t("corrido.inputAssets")}</PanelLabel>
          <div style={{ display: "grid", gap: 10 }}>
            <div>
              <div style={{ fontWeight: 700, color: C.white, marginBottom: 4, fontSize: 12 }}>{t("corrido.songLabel")}</div>
              <input id="corrido-song-upload" name="corridoSongUpload" type="file" data-consent-tool="corrido" accept="audio/*" onChange={e => {
                const picked = e.target.files?.[0] || null;
                setSong(picked);
                // A new file replaces a song kept for a build waiting on its avatar.
                if (picked) setSavedSong(null);
              }} style={{ fontSize: 13 }} />
              {songLabel && <div data-testid="corrido-song-name" style={{ fontSize: 13, color: C.green, marginTop: 3 }}>✓ {songLabel}</div>}
            </div>
            {artistMode && faceLockRequired && crew && (
              // REQUIRED: the avatar step REPLACES the close-up uploader. Pick
              // a ready avatar, or create one right here (the owner's photo
              // guidance, 8/16/24 photos); a new one trains while the choices
              // wait, and the build starts by itself when it is ready.
              <FaceLockCastStep
                crew={crew}
                pickable={castableFaceLocks}
                value={chosenFaceLock ? chosenFaceLock.id : null}
                onChange={(id) => { setFaceLockId(id); if (id) { stopFaceLockWait(); setFailedFaceLockId(null); } }}
                waitingId={waitingFaceLockId || failedFaceLockId}
                onCreated={onFaceLockCreated}
                onNoFace={() => { stopFaceLockWait(); setFaceLockId(null); setArtistMode(false); }}
                noFaceLabel={t("faceLock.required.corridoNoFace")}
                intro={t("faceLock.required.corridoIntro")}
                waitingNote={(name) => t("faceLock.required.corridoWaiting", { name })}
                testIdPrefix="corrido-avatar-step"
                palette={{ accent: C.cyan, accentSoft: "rgba(56,189,248,.08)", border: C.border, surface: C.surface, white: C.white, text: C.offwhite, muted: C.muted }}
              />
            )}
            {artistMode && !faceLockRequired && (
              <div>
                {/* FaceLock crew: pick a trained FaceLock and skip the close-ups.
                    Shown only when the account has one to cast; choosing it hides
                    the uploader below, and "New photos" brings it back as it was. */}
                {castableFaceLocks.length > 0 && (
                  <FaceLockPicker
                    avatars={castableFaceLocks}
                    value={chosenFaceLock ? chosenFaceLock.id : null}
                    onChange={setFaceLockId}
                    testIdPrefix="corrido-facelock"
                    palette={{ accent: C.cyan, accentSoft: "rgba(56,189,248,.08)", border: C.border, surface: C.surface, white: C.white, text: C.offwhite, muted: C.muted }}
                  />
                )}
                {!usingFaceLock && (
                  <>
                    <div style={{ fontWeight: 700, color: C.white, marginBottom: 4, fontSize: 12 }}>{t("corrido.photosLabel")} <span style={{ fontWeight: 400, color: C.muted }}>{t("corrido.photosRequired")}</span>
                    </div>
                    {/* Identity locks ONLY from tight close-ups — small/soft/wide
                        photos produce a confident lookalike, not the real person.
                        State the requirement plainly and up front (user directive
                        2026-07-22), not buried in a tooltip. */}
                    <div style={{ fontSize: 12, color: C.offwhite, lineHeight: 1.55, marginBottom: 6, borderRadius: 8, border: `1px solid ${C.gold}`, background: "rgba(227,179,65,.07)", padding: "8px 10px" }}>
                      <b style={{ color: C.gold }}>{t("corrido.photoRuleLead")}</b> {t("corrido.photoRuleUpload")}{" "}
                      <b style={{ color: C.white }}>{t("corrido.photoRuleCount")}</b> {t("corrido.photoRuleMiddle")}{" "}
                      <b style={{ color: C.white }}>{t("corrido.photoRuleNo")}</b>{t("corrido.photoRuleTail")}
                    </div>
                    <input id="corrido-photo-upload" name="corridoPhotoUpload" type="file" data-consent-tool="corrido" accept={CORRIDO_UPLOAD_ACCEPT} multiple style={{ fontSize: 13 }}
                      onChange={e => { addPhotos(e.target.files); e.currentTarget.value = ""; }} />
                    <div data-testid="corrido-photo-counter" style={{ fontSize: 12, fontWeight: 700, marginTop: 5, color: photos.length >= MIN_ARTIST_PHOTOS ? C.green : C.gold }}>
                      {photos.length === 0 ? t("corrido.noPhotosYet")
                        : photos.length < MIN_ARTIST_PHOTOS ? t("corrido.photosBelowMin", { have: photos.length, missing: MIN_ARTIST_PHOTOS - photos.length })
                        : `${t("corrido.photosAdded", { count: photos.length })}${photos.length < MAX_ARTIST_PHOTOS ? t("corrido.photosUpToSix") : ""}`}
                    </div>
                    {photos.length > 0 && (
                      <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginTop: 8 }}>
                        {photos.map((p, i) => {
                          const verdict = verdicts.find(v => v.name === p.name);
                          return (
                            <div key={`${p.name}_${p.size}`} style={{ position: "relative", width: 54 }}>
                              <img src={photoPreviews[i]} alt={p.name}
                                style={{ width: 54, height: 72, objectFit: "cover", borderRadius: 6, border: `1px solid ${verdict ? (verdict.ok ? C.green : C.red) : C.border}` }} />
                              <button onClick={() => setPhotos(prev => prev.filter((_, j) => j !== i))}
                                title={t("corrido.removePhoto", { name: p.name })}
                                style={{ position: "absolute", top: -6, right: -6, width: 16, height: 16, borderRadius: "50%", border: "none", background: C.red, color: C.white, fontSize: 11, lineHeight: "16px", cursor: "pointer", padding: 0 }}>✕</button>
                              {verdict && <div style={{ fontSize: 11, color: verdict.ok ? C.green : C.red, textAlign: "center" }}>{verdict.ok ? t("corrido.photoOk") : t("corrido.photoReplace")}</div>}
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </>
                )}
              </div>
            )}
            <div>
              <div style={{ fontWeight: 700, color: C.white, fontSize: 12, marginBottom: 6 }}>{t("corrido.pickStyle")}</div>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(148px, 1fr))", gap: 6 }}>
                {STYLE_CARDS.map((card) => {
                  const active = style === card.id;
                  return (
                    <button key={card.id} type="button" onClick={() => setStyle(card.id)}
                      data-testid={`corrido-style-${card.id}`}
                      style={{ display: "flex", alignItems: "center", gap: 8, textAlign: "left", borderRadius: 9, padding: "7px 9px", cursor: "pointer", background: active ? "rgba(56,189,248,.08)" : C.surface, border: `1px solid ${active ? C.cyan : C.border}` }}>
                      {card.previews.length > 0 ? (
                        <span style={{ display: "flex", gap: 2, flexShrink: 0 }}>
                          {card.previews.map((src) => (
                            <img key={src} src={src} alt="" style={{ width: 26, height: 36, objectFit: "cover", borderRadius: 4, border: `1px solid ${C.border}` }} />
                          ))}
                        </span>
                      ) : (
                        <span style={{ fontSize: 18, flexShrink: 0 }}>{card.emoji}</span>
                      )}
                      <span style={{ minWidth: 0 }}>
                        <span style={{ display: "block", fontSize: 13, fontWeight: 700, color: active ? C.cyan : C.white }}>{t(`corrido.styles.${card.id}`)}</span>
                        <span style={{ display: "block", fontSize: 11, color: C.muted }}>{t(`corrido.styles.${card.id}Desc`)}</span>
                      </span>
                    </button>
                  );
                })}
              </div>
              <div style={{ fontSize: 11, color: C.muted, marginTop: 4, lineHeight: 1.4 }}>{t("corrido.styleLibraryNote")}
              </div>
            </div>
            {/* The box steers the ARTIST shots only (owner, 2026-09-29): genre and
                lyrics drive the video; this is the artist on camera. A faceless
                video has no artist, so the box would do nothing there. */}
            {artistMode === true && (
            <div>
              <div style={{ fontWeight: 700, color: C.white, marginBottom: 4, fontSize: 13 }}>{t("corrido.describeVision")} <span style={{ fontWeight: 400, color: C.muted }}>{t("corrido.optional")}</span>
              </div>
              <textarea
                id="corrido-vision"
                name="corridoVision"
                value={beats}
                onChange={e => setBeats(e.target.value)}
                rows={4}
                placeholder={t("corrido.visionPlaceholder")} style={{ width: "100%", background: C.bg, border: `1px solid ${C.border}`, color: C.offwhite, borderRadius: 8, padding: "8px 10px", fontSize: 13, resize: "vertical", boxSizing: "border-box" }}
              />
              <div style={{ fontSize: 12, color: C.muted, marginTop: 3, lineHeight: 1.4 }}>{t(scenesPlan ? "corrido.visionHelpScenes" : "corrido.visionHelp")}
              </div>
              {visionRead && beats.trim() && <VisionPreview read={visionRead} plan={stylePlan} />}
            </div>
            )}
            {verdicts.length > 0 && verdicts.some(v => !v.ok) && (
              <div style={{ borderRadius: 8, border: `1px solid ${C.border}`, background: C.surface, padding: 8, display: "grid", gap: 3 }}>
                <div style={{ fontSize: 13, fontWeight: 700, color: C.white }}>{t("corrido.photoCheckResults")}</div>
                {verdicts.map((v, i) => (
                  <div key={i} style={{ fontSize: 13, display: "flex", gap: 6, alignItems: "flex-start" }}>
                    <span>{v.ok ? "" : ""}</span>
                    <span style={{ maxWidth: 160, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{v.name}</span>
                    <span style={{ color: C.muted }}>— {v.reason}</span>
                  </div>
                ))}
              </div>
            )}
            {phase === "inputs" && (() => {
              // REQUIRED: an artist build needs a chosen avatar and never photos.
              const needsAvatar = artistMode === true && faceLockRequired;
              const canBuild = hasSong && (!artistMode || usingFaceLock
                || (!needsAvatar && photos.length >= MIN_ARTIST_PHOTOS && photos.length <= MAX_ARTIST_PHOTOS));
              const blockedLabel = !hasSong ? t("corrido.addSongToContinue")
                : needsAvatar ? t(waitingFaceLockId ? "faceLock.required.corridoWaitingButton" : "faceLock.required.corridoPickToContinue")
                  : t("corrido.addMorePhotos", { count: Math.max(1, MIN_ARTIST_PHOTOS - photos.length) });
              return (
                <button onClick={() => void start()} disabled={!canBuild} data-testid="corrido-build"
                  title={canBuild ? undefined : (!hasSong ? t("corrido.addSongFirst") : needsAvatar ? t("faceLock.required.corridoNeedAvatar") : t("corrido.needPhotosShort"))}
                  style={{ justifySelf: "start", borderRadius: 10, border: "none", background: canBuild ? `linear-gradient(135deg,${C.pink},${C.purple})` : "rgba(255,255,255,.08)", color: canBuild ? C.white : C.muted, fontWeight: 700, letterSpacing: 1, padding: "10px 18px", fontSize: 12, cursor: canBuild ? "pointer" : "default", fontFamily: "'Rajdhani', sans-serif" }}>
                  {canBuild ? t("corrido.buildMyStoryboard") : blockedLabel}
                </button>
              );
            })()}
          </div>
        </div>
      )}

      {/* collapsed inputs summary once the storyboard exists */}
      {phase !== "mode" && phase !== "inputs" && !inputsOpen && (
        <button onClick={() => setInputsOpen(true)}
          style={{ display: "flex", alignItems: "center", gap: 8, borderRadius: 9, border: `1px solid ${C.border}`, background: C.surface, color: C.muted, padding: "6px 10px", fontSize: 13, cursor: "pointer", textAlign: "left" }}>
          <span> {songLabel || t("corrido.songFallback")}</span>
          {artistMode
            ? (usingFaceLock && chosenFaceLock ? <span>{t("faceLock.picker.summary", { name: chosenFaceLock.name })}</span> : <span>{t("corrido.photosCount", { count: photos.length })}</span>)
            : <span>{t("corrido.faceless")}</span>}
          <span>·  {styleLabelFor(t, style)}</span>
          <span style={{ marginLeft: "auto", color: C.cyan }}>{t("corrido.viewInputs")}</span>
        </button>
      )}

      {/* status / progress */}
      {(phase === "working" || phase === "animating" || phase === "assembling" || phase === "revising") && (
        <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 12, padding: 12 }}>
          {/* FIRST-PHASE EXPECTATION (field report 2026-07-21: "it sits at 8% for
              40+ minutes with no notice"). The storyboard build generates every
              still fresh — identity-locked artist scenes + original b-roll — so it
              is genuinely long. Tell the user the expected wait and WHY up front,
              and that it keeps working if they leave. */}
          {phase === "working" && (
            <div style={{ marginBottom: 10, borderRadius: 10, border: `1px solid ${C.gold}`, background: "rgba(227,179,65,.07)", padding: "10px 12px" }}>
              <div style={{ fontSize: 13, fontWeight: 800, color: C.gold, letterSpacing: 0.5, marginBottom: 4 }}>
                {t("corrido.waitTitle")}
              </div>
              <div style={{ fontSize: 12.5, color: C.offwhite, lineHeight: 1.6 }}>
                {t("corrido.waitBody1")} <b style={{ color: C.white }}>{t("corrido.waitBody1Bold")}</b> {t("corrido.waitBody1Tail")}
                <br />
                {t("corrido.waitBody2")} <b style={{ color: C.white }}>{t("corrido.waitBody2Bold")}</b>{t("corrido.waitBody2Tail")}
                <br />
                {t("corrido.waitBody3")} <b style={{ color: C.white }}>{t("corrido.waitBody3Bold")}</b> {t("corrido.waitBody3Tail")}
              </div>
            </div>
          )}
          {/* THE SHARED BAR (BuildProgress). It moves every few hundred ms toward
              where the job should be by now — never past what the evidence allows,
              never backwards — and says the stage in plain words, how many
              pictures or clips exist, the time left (counting down between polls),
              the elapsed clock, and that it keeps working if they leave. The same
              bar, from the same frame, sits on the board. */}
          <BuildProgress
            jobKey={buildKey}
            numbers={buildNumbers}
            receivedAt={buildReceivedAt}
            label={note || null}
            unit={buildUnit}
            testId="corrido-build-progress"
          />
          {reelItems.length > 0 && (
            <WaitReel items={reelItems} title={t("corrido.waitReel.title")} label={t("corrido.waitReel.label")} />
          )}
          <div style={{ display: "flex", alignItems: "center", marginTop: 10 }}>
            <button
              onClick={cancelRender}
              disabled={cancelling}
              style={{
                background: "transparent", color: C.muted, border: `1px solid ${C.border}`,
                borderRadius: 8, padding: "4px 10px", fontSize: 12, fontWeight: 700,
                cursor: cancelling ? "default" : "pointer", opacity: cancelling ? 0.6 : 1,
              }}
            >
              {cancelling ? t("corrido.cancelling") : t("corrido.cancelRender")}
            </button>
          </div>
        </div>
      )}

      {/* The progress panel and the done panel print the note themselves; this
          block covers every other phase, so a line never shows twice. */}
      {note && !["working", "animating", "assembling", "revising", "done"].includes(phase) && (
        <div style={{ fontSize: 12, color: C.offwhite }}>
          {note}
          {supportCode && (
            <div style={{ marginTop: 4, fontSize: 11, color: C.muted }}>
              {t("corrido.quoteSupportCode")} <code style={{ color: C.offwhite }}>{supportCode}</code>
            </div>
          )}
        </div>
      )}

      {/* A redraw or a new take is a real wait too (minutes, not seconds): the
          same moving bar, under the line above that already names the step. */}
      {activeJobId && ((phase === "board" && regenning !== null) || (phase === "clips" && rerolling !== null)) && (
        <BuildProgress
          jobKey={buildKey}
          numbers={buildNumbers}
          receivedAt={buildReceivedAt}
          unit={buildUnit}
          hideLabel
          compact
          testId="corrido-job-progress"
        />
      )}

      {/* BOARD POINTER — editing, approval, regenerate, reorder and render all
          live on the STORYBOARD BOARD now (the canvas to the left), not here in
          the chat (user directive 2026-07-21). The chat stays the conversation +
          the money gate; the board is where you touch the clips. */}
      {(phase === "board" || phase === "animating") && shots.length > 0 && (
        <div style={{ background: C.card, border: `1px solid ${C.borderHi}`, borderRadius: 12, padding: 12, display: "flex", gap: 12, alignItems: "center", flexWrap: "wrap" }}>
          {selectedShot && (
            <AuthImg src={imgUrl(selectedShot.image)} alt={t("corrido.shotAlt", { n: selectedShot.idx + 1 })}
              style={{ width: 64, aspectRatio: "9 / 16", objectFit: "cover", borderRadius: 8, border: `1px solid ${C.borderHi}`, flexShrink: 0 }} />
          )}
          <div style={{ flex: 1, minWidth: 180 }}>
            <div style={{ fontSize: 13, fontWeight: 800, color: C.white, marginBottom: 3 }}>
              {t("corrido.boardPointerTitle")}
            </div>
            <div style={{ fontSize: 12, color: C.muted, lineHeight: 1.5 }}>
              {phase === "board"
                ? <>{t("corrido.boardPointerBody1")} <b style={{ color: C.offwhite }}>{t("corrido.boardPointerBoardBold")}</b> {t("corrido.boardPointerBody2")} <b style={{ color: C.offwhite }}>{t("corrido.boardPointerRegenBold")}</b> {t("corrido.boardPointerBody3")}</>
                : <>{t("corrido.boardPointerAnimating")}</>}
            </div>
            {/* The money, stated where the decision is made: the board was free,
                the animation is the priced step, and a confirmation comes first. */}
            {phase === "board" && (
              <div data-testid="corrido-board-cost" style={{ fontSize: 12, color: C.gold, marginTop: 6 }}>
                {t("corrido.boardCostNote", { count: renderCost.toLocaleString() })}
              </div>
            )}
          </div>
        </div>
      )}

      {/* STAGE 2 — ANIMATED CLIP REVIEW. The stills gate what got animated; THIS
          gates what gets assembled. Every documented failure (morphing faces,
          floating trucks, giant props) is born at animation time, so the customer
          watches every clip before the video exists. */}
      {/* Clip review is on the BOARD now (ClipsReviewNode). This chat pointer
          replaces the big in-chat panel; the old panel below is kept but never
          rendered ({false}) so the reroll/replace/assemble handlers still have a
          single source of truth. */}
      {phase === "clips" && (
        <div style={{ background: C.card, border: `1px solid ${C.borderHi}`, borderRadius: 12, padding: 12, display: "flex", gap: 12, alignItems: "center", flexWrap: "wrap" }}>
          <div style={{ flex: 1, minWidth: 180 }}>
            <div style={{ fontSize: 13, fontWeight: 800, color: C.white, marginBottom: 3 }}>{t("corrido.clipsPointerTitle")}</div>
            <div style={{ fontSize: 12, color: C.muted, lineHeight: 1.5 }}>
              {t("corrido.clipsPointerBody")}{" "}
              <b style={{ color: C.offwhite }}>{t("corrido.clipsPointerBoardBold")}</b> {t("corrido.clipsPointerTail", { count: MAX_REROLLS - rerollsUsed })}
            </div>
          </div>
        </div>
      )}

      {/* CREDIT GATE — the money confirmation, opened by the board's Render button
          (studio bus → onRender). The board shows the same question under that
          button; this is the chat's copy. It sits ABOVE the Continue and the
          owner's Recover cards, which used to push it out of sight. */}
      {phase === "board" && confirmingRender && (
            <div data-testid="corrido-render-confirm" ref={confirmBoxRef} style={{ borderRadius: 12, border: `1px solid ${C.gold}`, background: "rgba(227,179,65,.08)", padding: 12, display: "grid", gap: 8 }}>
              <div style={{ fontSize: 12, fontWeight: 700, color: C.white }}>{t("corrido.confirmCredits")}
              </div>
              <div style={{ fontSize: 13, color: C.offwhite, lineHeight: 1.6 }}>{t("corrido.renderCostsLead")} <b style={{ color: C.gold }}>{t("corrido.renderCostsCredits", { count: (renderCost ?? 0).toLocaleString() })}</b>.
                {" "}{t("corrido.yourBalance")} <b style={{ color: C.gold }}>{balance === null ? "—" : balance.toLocaleString()}</b>
                {balance !== null && renderCost !== null && (
                  balance >= renderCost
                    ? <> → <b style={{ color: C.green }}>{t("corrido.afterBalance", { count: (balance - renderCost).toLocaleString() })}</b>.</>
                    : <> — <b style={{ color: C.red }}>{t("corrido.notEnoughTopUp")}</b>.</>
                )}
                {" "}{t("corrido.refundNote")}
              </div>
              <label style={{ display: "flex", gap: 8, alignItems: "flex-start", fontSize: 13, color: C.offwhite, lineHeight: 1.5, cursor: "pointer" }}>
                <input type="checkbox" data-testid="corrido-review-clips" checked={reviewClips}
                  onChange={(e) => setReviewClips(e.target.checked)} style={{ marginTop: 3 }} />
                <span>{t("corrido.reviewClipsOption")}</span>
              </label>
              <SiteMarkSwitch testId="corrido-site-mark" style={{ color: C.offwhite }} />
              <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                <button onClick={confirmRun}
                  disabled={balance !== null && renderCost !== null && balance < renderCost}
                  data-testid="corrido-confirm-run" style={{ borderRadius: 8, border: "none", background: `linear-gradient(135deg,${C.pink},${C.purple})`, color: C.white, fontWeight: 700, letterSpacing: 1, padding: "9px 16px", fontSize: 13, cursor: "pointer", opacity: balance !== null && renderCost !== null && balance < renderCost ? 0.5 : 1 }}>{t("corrido.confirmAndRun")}
                </button>
                {balance !== null && renderCost !== null && balance < renderCost && (
                  <button type="button" data-testid="corrido-render-buy" onClick={() => openPaySheet({ need: renderCost, have: balance, label: t("corrido.payForRender") })}
                    style={{ borderRadius: 8, border: `1px solid ${C.gold}`, background: "transparent", color: C.gold, padding: "9px 14px", fontSize: 13, fontWeight: 700, cursor: "pointer" }}>{t("corrido.buyCredits")}
                  </button>
                )}
                <button onClick={dismissRender}
                  style={{ borderRadius: 8, border: `1px solid ${C.border}`, background: "transparent", color: C.muted, padding: "9px 14px", fontSize: 13, cursor: "pointer" }}>{t("corrido.keepEditing")}
                </button>
              </div>
            </div>
      )}

      {/* A VIDEO THAT STOPPED EARLY — its finished scenes are saved; Continue
          renders only the rest (free while the first charge stands). */}
      {videoPartial && (phase === "board" || phase === "clips") && (
        <div data-testid="corrido-continue-video" style={{ borderRadius: 12, border: `1px solid ${C.gold}`, background: "rgba(227,179,65,.08)", padding: 12, display: "grid", gap: 8 }}>
          <div style={{ fontSize: 13, fontWeight: 800, color: C.white }}>{t("corrido.partialTitle")}</div>
          <div style={{ fontSize: 13, color: C.offwhite, lineHeight: 1.6 }}>{partialText(videoPartial)}</div>
          <div>
            {(() => {
              const short = !videoPartial.free && balance !== null && balance < renderCost;
              return (
                <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
                  <button onClick={continueVideo} data-testid="corrido-continue-run" disabled={continueAsk && short}
                    style={{ borderRadius: 8, border: "none", background: `linear-gradient(135deg,${C.pink},${C.purple})`, color: C.white, fontWeight: 700, letterSpacing: 1, padding: "9px 16px", fontSize: 13, cursor: continueAsk && short ? "default" : "pointer", opacity: continueAsk && short ? 0.5 : 1 }}>
                    {continueAsk && !videoPartial.free ? t("corrido.confirmAndRun") : t("corrido.partialContinue")}
                  </button>
                  {continueAsk && !videoPartial.free && (
                    <span data-testid="corrido-continue-price" style={{ fontSize: 12, color: short ? C.red : C.gold }}>
                      {t("corrido.renderCostsLead")} {t("corrido.renderCostsCredits", { count: renderCost.toLocaleString() })}. {t("corrido.yourBalance")} {balance === null ? "—" : balance.toLocaleString()}{short ? ` — ${t("corrido.notEnoughTopUp")}` : ""}
                    </span>
                  )}
                  {continueAsk && short && (
                    <button type="button" data-testid="corrido-continue-buy" onClick={() => openPaySheet({ need: renderCost, have: balance ?? 0, label: t("corrido.payForRender") })}
                      style={{ borderRadius: 8, border: `1px solid ${C.gold}`, background: "transparent", color: C.gold, padding: "9px 14px", fontSize: 13, fontWeight: 700, cursor: "pointer" }}>{t("corrido.buyCredits")}</button>
                  )}
                </div>
              );
            })()}
          </div>
        </div>
      )}

      {/* OWNER ONLY — a video that was lost before it was delivered. Look first
          (nothing changes), then bring the scenes back onto this board. */}
      {crew?.admin && runId && !videoPartial && (phase === "board" || phase === "clips") && (
        <div data-testid="corrido-recover" style={{ borderRadius: 12, border: `1px dashed ${C.border}`, padding: 12, display: "grid", gap: 8 }}>
          <div style={{ fontSize: 12, fontWeight: 700, color: C.muted }}>{t("corrido.recoverTitle")}</div>
          <div style={{ fontSize: 12, color: C.muted, lineHeight: 1.5 }}>{recover?.note || t("corrido.recoverBody")}</div>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            <button onClick={() => void recoverScan()} disabled={!!recover?.busy} data-testid="corrido-recover-scan"
              style={{ borderRadius: 8, border: `1px solid ${C.border}`, background: "transparent", color: C.offwhite, padding: "8px 14px", fontSize: 12, cursor: recover?.busy ? "default" : "pointer", opacity: recover?.busy ? 0.6 : 1 }}>
              {recover?.busy ? t("corrido.recoverScanning") : t("corrido.recoverScan")}
            </button>
            {!recover?.busy && (recover?.found || 0) > 0 && (
              <button onClick={() => void recoverNow()} data-testid="corrido-recover-run"
                style={{ borderRadius: 8, border: "none", background: `linear-gradient(135deg,${C.pink},${C.purple})`, color: C.white, fontWeight: 700, padding: "8px 14px", fontSize: 12, cursor: "pointer" }}>
                {t("corrido.recoverRun")}
              </button>
            )}
          </div>
        </div>
      )}

      {/* done + the ONE FREE REVISION PASS (the productized emergency plan:
          drop bad clips in minutes, re-take a few in ~12 min each — never a
          refund, never a 6-hour re-render) */}
      {phase === "done" && (
        <div data-testid="corrido-done" style={{ background: C.card, border: `1px solid rgba(52,211,153,.35)`, borderRadius: 12, padding: 12, display: "grid", gap: 10 }}>
          <PanelLabel>{t("corrido.finalVideo")}</PanelLabel>
          <div>{note}</div>
          {/* The player, a real download and a copyable link — the three things a
              finished video needs. All three exist ONLY once result_url arrived
              (assembleDone refuses to enter this phase without it). */}
          {videoUrl && (
            <>
              {playableVideo
                ? <video data-testid="corrido-final-player" src={playableVideo} controls playsInline style={{ width: "100%", maxWidth: 320, borderRadius: 10, border: `1px solid ${C.border}`, background: "#000" }} />
                : <span aria-hidden style={{ display: "inline-block", width: "100%", maxWidth: 320, aspectRatio: "9 / 16", borderRadius: 10, background: "rgba(255,255,255,.04)" }} />}
              <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                {/* Download only once the file is in hand: the proxy URL is
                    sign-in gated and answers 401 from a bare anchor. */}
                {playableVideo ? (
                  <a
                    href={playableVideo}
                    download={`corrido-${String(runId || "video").slice(0, 8)}.mp4`}
                    // A beacon only: the download itself is the anchor's, untouched.
                    onClick={() => sendFeedback("video_downloaded")}
                    data-testid="corrido-download"
                    style={{ borderRadius: 8, border: "none", background: `linear-gradient(135deg,${C.pink},${C.purple})`, color: C.white, fontWeight: 700, letterSpacing: 1, padding: "9px 16px", fontSize: 13, textDecoration: "none" }}>
                    {t("corrido.downloadVideo")}
                  </a>
                ) : (
                  <span data-testid="corrido-download-pending" style={{ borderRadius: 8, border: `1px solid ${C.border}`, color: C.muted, padding: "9px 16px", fontSize: 13 }}>
                    {t("corrido.downloadVideo")}…
                  </span>
                )}
                {/* No "copy link": the video is served only through this site
                    behind the customer's sign-in, so there is no link a
                    stranger could open. A public share page is a separate
                    piece of work; until it exists the button would copy a
                    link that answers 401. Share sends the file itself. */}
                {shareFile && (
                  <button type="button" onClick={shareVideo} data-testid="corrido-share"
                    style={{ borderRadius: 8, border: `1px solid ${C.cyan}`, background: "transparent", color: C.cyan, fontWeight: 700, letterSpacing: 1, padding: "9px 16px", fontSize: 13, cursor: "pointer" }}>
                    {t("corrido.shareVideo")}
                  </button>
                )}
              </div>
              {shareNote && <div style={{ fontSize: 12, color: C.muted }}>{shareNote}</div>}
              {/* The link back to this studio, with the sharer's referral code. */}
              <ShareCard studio="music" mediaUrl={playableVideo || undefined} fileName={`corrido-${String(runId || "video").slice(0, 8)}.mp4`} />
              {/* One tap, optional: teaches the tool which videos people like. */}
              <div data-testid="corrido-rate" style={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 6, fontSize: 13, color: C.offwhite }}>
                {videoRating ? (
                  <span data-testid="corrido-rate-thanks" style={{ color: C.muted, fontSize: 12 }}>{t("corridoLearning.rateThanks")}</span>
                ) : (
                  <>
                    <span>{t("corridoLearning.rateAsk")}</span>
                    {(["fire", "good", "off"] as const).map((r) => (
                      <button key={r} type="button" onClick={() => rateVideo(r)} data-testid={`corrido-rate-${r}`}
                        style={{ borderRadius: 999, border: `1px solid ${C.border}`, background: "transparent", color: C.offwhite, padding: "5px 12px", fontSize: 12, fontWeight: 700, cursor: "pointer" }}>
                        {t(`corridoLearning.rate.${r}`)}
                      </button>
                    ))}
                  </>
                )}
              </div>
            </>
          )}
          {!fixMode && !revisionUsed && lookAt.length > 0 && (
            <div data-testid="corrido-done-attention" style={{ fontSize: 13, color: C.offwhite, lineHeight: 1.5 }}>
              {lookAt.moved.length > 0 && <div>{t("corrido.doneMoved", { count: lookAt.moved.length, scenes: lookAt.moved.join(", ") })}</div>}
              {lookAt.replaced.length > 0 && <div>{t("corrido.doneReplaced", { count: lookAt.replaced.length, scenes: lookAt.replaced.join(", ") })}</div>}
              {lookAt.flagged.length > 0 && <div>{t("corrido.doneFlagged", { count: lookAt.flagged.length, scenes: lookAt.flagged.join(", ") })}</div>}
              <div style={{ color: C.gold }}>{t("corrido.doneAttentionFix")}</div>
            </div>
          )}
          {!fixMode && !revisionUsed && clips.length > 0 && (
            <button onClick={() => {
              setFixMode(true);
              // The scenes the engine stood in for or flagged start marked
              // for a new take; the customer can unmark them.
              const flagged = clips.filter(needsLook);
              setFixSelection(Object.fromEntries(flagged.map((c) => [c.idx, "reroll" as const])));
              setFixReasons({});
              // The first one the engine marked shows its "what's wrong" chips.
              setFixFocus(flagged.length ? flagged[0].idx : null);
            }}
              data-testid="corrido-request-fixes" style={{ justifySelf: "start", borderRadius: 9, border: `1px solid ${C.gold}`, background: "rgba(227,179,65,.08)", color: C.gold, padding: "8px 14px", fontSize: 13, fontWeight: 700, cursor: "pointer" }}>{t("corrido.requestFixes")}
            </button>
          )}
          {revisionUsed && (
            <div style={{ fontSize: 12, color: C.muted }}>{t("corrido.revisionUsed")}</div>
          )}
          {fixMode && (
            <div style={{ borderRadius: 10, border: `1px solid ${C.gold}`, background: "rgba(227,179,65,.06)", padding: 10, display: "grid", gap: 8 }}>
              <div style={{ fontSize: 12, fontWeight: 700, color: C.white }}>{t("corrido.markClips")}</div>
              <div style={{ fontSize: 13, color: C.offwhite, lineHeight: 1.5 }}>{t("corrido.markHelpLead")} <b style={{ color: C.red }}>{t("corrido.markHelpRemove")}</b> {t("corrido.markHelpMiddle")}{" "}
                <b style={{ color: C.cyan }}>{t("corrido.markHelpNewTake")}</b> {t("corrido.markHelpTail")}
              </div>
              <div style={{ display: "flex", gap: 6, overflowX: "auto", paddingBottom: 6 }}>
                {clips.map((c, pos) => {
                  const mark = fixSelection[c.idx];
                  return (
                    <button key={c.idx} data-testid={`corrido-fix-clip-${c.idx}`} data-mark={mark || ""}
                      onClick={() => {
                        // remove -> new take -> unmarked, as the help above says,
                        // on every tap. The chips follow the clip tapped last
                        // while it is marked; unmarking drops them.
                        const nextMark = !mark ? "exclude" : mark === "exclude" ? "reroll" : null;
                        setFixSelection(prev => {
                          const next = { ...prev };
                          if (nextMark) next[c.idx] = nextMark; else delete next[c.idx];
                          return next;
                        });
                        if (nextMark) setFixFocus(c.idx);
                        else {
                          setFixFocus((f) => (f === c.idx ? null : f));
                          setFixReasons((prev) => { const next = { ...prev }; delete next[c.idx]; return next; });
                        }
                      }}
                      aria-current={mark && fixFocus === c.idx ? true : undefined}
                      style={{ position: "relative", flexShrink: 0, padding: 0, borderRadius: 8, overflow: "hidden", cursor: "pointer", border: `2px solid ${mark === "exclude" ? C.red : mark === "reroll" ? C.cyan : C.border}`, outline: mark && fixFocus === c.idx ? `2px solid ${C.gold}` : "none", outlineOffset: 1, background: C.bg }}>
                      {c.image
                        ? <AuthImg src={imgUrl(c.image)} alt={t("corrido.clipAlt", { n: pos + 1 })} loading="lazy" style={{ width: 48, height: 84, objectFit: "cover", display: "block" }} />
                        : <span style={{ display: "block", width: 48, height: 84 }} />}
                      <span style={{ position: "absolute", top: 2, left: 2, background: "rgba(0,0,0,.72)", color: C.white, fontSize: 11, fontWeight: 700, borderRadius: 3, padding: "1px 4px" }}>{pos + 1}</span>
                      {mark && (
                        <span style={{ position: "absolute", bottom: 2, left: 2, right: 2, textAlign: "center", background: mark === "exclude" ? "rgba(255,61,90,.9)" : "rgba(56,189,248,.9)", color: "#0a0a10", fontSize: 11, fontWeight: 800, borderRadius: 3, padding: "1px 2px" }}>
                          {mark === "exclude" ? t("corrido.markRemove") : t("corrido.markNewTake")}
                        </span>
                      )}
                    </button>
                  );
                })}
              </div>
              {fixFocus !== null && fixSelection[fixFocus] && (
                <ReasonChips
                  kind="clips" testId="corrido-fix-reasons"
                  heading={t("corridoLearning.fixReasonsHeading", { n: Math.max(0, clips.findIndex((c) => c.idx === fixFocus)) + 1 })}
                  value={fixReasons[fixFocus] || []}
                  onChange={(next) => setFixReasons((prev) => ({ ...prev, [fixFocus]: next }))}
                />
              )}
              <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                <button onClick={() => void revise()}
                  disabled={Object.keys(fixSelection).length === 0}
                  data-testid="corrido-revise-run" style={{ borderRadius: 8, border: "none", background: `linear-gradient(135deg,${C.pink},${C.purple})`, color: C.white, fontWeight: 700, letterSpacing: 1, padding: "9px 16px", fontSize: 13, cursor: "pointer", opacity: Object.keys(fixSelection).length === 0 ? 0.5 : 1 }}>{t("corrido.fixMyVideo")}
                </button>
                <button onClick={() => { setFixMode(false); setFixSelection({}); setFixReasons({}); setFixFocus(null); }}
                  style={{ borderRadius: 8, border: `1px solid ${C.border}`, background: "transparent", color: C.muted, padding: "9px 14px", fontSize: 13, cursor: "pointer" }}>{t("corrido.neverMind")}
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
